import { AnalysisStopNotice } from "./AnalysisStopNotice";
import { AutonomousProcessing, uploadAutonomous, useAutonomousUploads } from "./AutonomousProcessing";
import { FinalReviewReport } from "./FinalReviewReport";
import { CalculationGuide } from "./CalculationNotice";
import { calculationGuide } from "./calculationGuide";
import { SourceStructure } from "./SourceStructure";
import { targetedDelivery } from "./targetedDelivery";
import { SpartakEstimatePreparation } from "./SpartakEstimatePreparation";
import { firstRevisionWithWorks } from "./defaultRevision";
import { jobKindText, jobStatusText } from "./jobs";
import { PdfPager } from "./PdfPager";
import { useViewTiming } from "./useViewTiming";
import { EstimateComparison } from "./EstimateComparison";
import { EstimateParameterChanges } from "./EstimateSettings";
import { DesignerReview } from "./DesignerReview";
import { baseText } from "./exportBase";
import { SvorPanel } from "./SvorPanel";
import { NormResources } from "./NormResources";
import { NormAlternatives } from "./NormAlternatives";
import {CadPages, type CadFocus} from "./CadPages";
import {ReviewRegister, WorkDetails, WorkProducts, TypeSummaryPanel} from "./ReviewPanels";
import {WorkAttentionPanel} from "./WorkAttentionPanel";
import {RevisionActionView} from "./RevisionActionView";
import {revisionDelta, revisionKindLabels} from "./revisionAttention";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleDollarSign,
  ChevronDown,
  Download,
  FileText,
  FolderOpen,
  Layers3,
  LoaderCircle,
  Maximize2,
  Plus,
  RefreshCw,
  Search,
  Upload,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  amount,
  ApiError,
  categories,
  call,
  client,
  result,
  retryDelay,
  retryTransient,
  statusLabel,
  type Project,
  type Revision,
  type Schema,
  type Source,
  type User,
  type Work,
} from "./api";
import { buildingChange, categoryOptions, parseSections, sectionOptions, sectionsInUse } from "./worklist";
import { placementLabels } from "./roomSummary";
import "@fontsource/golos-text/cyrillic-400.css";
import "@fontsource/golos-text/cyrillic-500.css";
import "@fontsource/golos-text/cyrillic-600.css";
import "@fontsource/golos-text/latin-400.css";
import "@fontsource/golos-text/latin-500.css";
import "@fontsource/golos-text/latin-600.css";
import "./style.css";
import { PdfSource } from "./PdfSource";
import { pdfRegions } from "./pdfRegions";
import { pdfMarkerNotice } from "./markerNotice";
import type { ViewCenter, ViewMemory } from "./viewState";
import { emptyScope, exportBody, exportCounts, formatNames, includedWorks, packageIncludesSvor, scopeOptions, scopeText, selectionLabel as exportSelectionLabel, type ExportFormat, type ScopeKey } from "./exportScope";
import { documentNote, documentWorks, type DocumentDelivery } from "./documentDelivery";
import { SourcePicker, sourcePageLabel, useDocumentSources, useSourceLabels } from "./SourcePicker";
import { ComplexNorm } from "./ComplexNorm";
import { normCode } from "./normCode";
import { splitNotes } from "./analysisNotes";
import { AnalysisEstimate } from "./AnalysisEstimate";
import { providerDescription, providerLabel } from "./analysisLaunch";
import { EventMeta, HistoryFilters, type HistoryFilter } from "./HistoryMeta";
import { changedKeys, eventSummary, groupActions, reasonText, successorsText } from "./history";
import {readProtocolView,writeProtocolView,type ProtocolView} from './protocolView';

const cache = new QueryClient({
  defaultOptions: { queries: { retry: retryTransient, retryDelay, staleTime: 10000 } },
});
const qs = new URLSearchParams(location.search);
type Run = (action: () => Promise<unknown>) => Promise<void>;
const ActionContext = React.createContext<{error: unknown; busy: boolean}>({error: null, busy: false});

function Spinner() {
  return <LoaderCircle className="spin" size={16} aria-label="Загрузка" />;
}
function ErrorText({ error }: { error: unknown }) {
  return error ? (
    <p className="error" role="alert">
      {error instanceof Error ? error.message : String(error)}
    </p>
  ) : null;
}
function Modal({
  title,
  children,
  close,
  wide = false,
  fullScreen = false,
}: {
  title: string;
  children: React.ReactNode;
  close: () => void;
  wide?: boolean;
  fullScreen?: boolean;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const action = React.useContext(ActionContext);
  useEffect(() => {
    dialog.current?.showModal();
    return () => dialog.current?.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className={fullScreen ? "modal source-fullscreen" : wide ? "modal wide" : "modal"}
      aria-label={title}
      // React state is the only owner of «open»: the dialog never closes natively behind it.
      onCancel={(e) => { e.preventDefault(); close(); }}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <header>
        <h2>{title}</h2>
        <button className="icon" aria-label="Закрыть" onClick={close}>
          <X size={20} />
        </button>
      </header>
      <ErrorText error={action.error} />
      <fieldset className="modal-content" disabled={action.busy}>{children}</fieldset>
    </dialog>
  );
}
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function App() {
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => result(client.GET("/api/v1/me")),
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [projectId, setProjectId] = useState(qs.get("project") || "");
  const [create, setCreate] = useState(false);
  const projects = useQuery({
    queryKey: ["projects"],
    enabled: !!me.data,
    queryFn: () => result(client.GET("/api/v1/projects")),
  });
  const catalogs = useQuery({
    queryKey: ["catalogs"],
    enabled: !!me.data,
    queryFn: () => result(client.GET("/api/v1/catalogs")),
  });
  const run: Run = async (action) => {
    setError(null);
    setBusy(true);
    try {
      await action();
      await cache.invalidateQueries({
        predicate: (q) => q.queryKey[0] !== "me",
      });
    } catch (e) {
      setError(e);
      if(e instanceof ApiError && e.status===409) await cache.invalidateQueries({predicate:q=>q.queryKey[0]!=="me"});
    } finally {
      setBusy(false);
    }
  };
  if (me.isLoading)
    return (
      <main className="welcome">
        <Spinner />
      </main>
    );
  if (me.error && !(me.error instanceof ApiError && me.error.status === 401))
    return <main className="welcome"><h1>Не удалось подключиться</h1><ErrorText error={me.error} />
      <button onClick={() => void me.refetch()} disabled={me.isFetching}>Повторить подключение</button></main>;
  if (!me.data) return <Login />;
  const project = projects.data?.find((item) => item.id === projectId);
  return (
    <ActionContext.Provider value={{error, busy}}><div className="app-shell">
      <header className="app-header">
        <button
          className="brand"
          onClick={() => {
            setProjectId("");
            history.replaceState(null, "", "/");
          }}
        >
          <span className="brand-mark">i</span> IT Ski{" "}
          <span className="brand-caption">Ведомость работ</span>
        </button>
        <div className="header-end">
          {busy && <Spinner />}
          <span>{me.data.name}</span>
          <button
            className="quiet"
            onClick={() =>
              run(async () => {
                await result(client.POST("/api/v1/logout"));
                cache.clear();
                location.assign("/");
              })
            }
          >
            Выйти
          </button>
        </div>
      </header>
      {error != null && (
        <div className="notice error" role="alert">
          <span>{error instanceof Error ? error.message : String(error)}</span>
          <button
            className="icon"
            aria-label="Закрыть сообщение"
            onClick={() => setError(null)}
          >
            <X size={18} />
          </button>
        </div>
      )}
      {!projects.data && (projects.isPending || projects.error) ? (
        <main className="welcome">
          {projects.error ? <><h1>Не удалось загрузить проекты</h1><ErrorText error={projects.error} />
            <button onClick={() => void projects.refetch()} disabled={projects.isFetching}>Повторить загрузку</button></>
            : <><Spinner /><p>Загружаем проекты…</p></>}
        </main>
      ) : project ? (
        <Workspace
          key={project.id}
          project={project}
          user={me.data}
          run={run}
          back={() => { setProjectId(""); history.replaceState(null, "", "/"); }}
          catalogs={catalogs.data || []}
        />
      ) : (
        <main className="project-home">
          <div className="eyebrow">Рабочее место сметчика</div>
          <div className="page-title">
            <div>
              <h1>От проекта к объёмам.</h1>
              <p>
                Исходные данные, проверяемые работы и ваши решения — в одном
                месте.
              </p>
            </div>
            {me.data.role !== "viewer" && (
              <button className="primary" onClick={() => setCreate(true)}>
                <Plus size={18} />
                Новый проект
              </button>
            )}
          </div>
          <ErrorText error={projects.error} />
          <div className="project-list">
            <div className="list-label">
              <span>Проект</span>
              <span>Разделы</span>
              <span>Создан</span>
            </div>
            {projects.data?.map((item) => (
              <button
                className="project-row"
                key={item.id}
                onClick={() => {
                  setProjectId(item.id);
                  history.replaceState(null, "", `/?project=${item.id}`);
                }}
              >
                <span>
                  <FolderOpen size={23} />
                  <strong>{item.name}</strong>
                </span>
                <span>{(item.sections ?? []).join(" · ")}</span>
                <span>
                  {new Date(item.created_at).toLocaleDateString("ru-RU")}
                  <ArrowRight size={18} />
                </span>
              </button>
            ))}
            {!projects.data?.length && (
              <div className="empty">
                <FolderOpen size={32} />
                <h2>Начните с исходных данных</h2>
                <p>
                  Создайте проект и загрузите чертежи, ведомости или архив
                  целиком.
                </p>
              </div>
            )}
          </div>
          <p className="home-note">АР · ЭОМ · Сравнение редакций</p>
        </main>
      )}
      {create && (
        <ProjectForm
          catalogs={catalogs.data || []}
          close={() => setCreate(false)}
          run={run}
          onCreated={(id) => { history.replaceState(null, "", `/?project=${id}`); setProjectId(id); }}
        />
      )}
    </div></ActionContext.Provider>
  );
}

function Login() {
  const [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  return (
    <main className="login">
      <div className="login-story">
        <div className="brand">
          <span className="brand-mark">i</span> IT Ski
        </div>
        <h1>
          Каждый объём
          <br />
          имеет основание.
        </h1>
        <p>
          Рабочее место для ведомостей АР и ЭОМ.
          <br />
          От чертежа до проверенного результата.
        </p>
        <div className="paper-motif" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
      <form
        className="login-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          const data = new FormData(e.currentTarget);
          try {
            await result(
              client.POST("/api/v1/login", {
                body: {
                  email: String(data.get("email")),
                  password: String(data.get("password")),
                },
              }),
            );
            await cache.invalidateQueries();
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="eyebrow">Добро пожаловать</div>
        <h2>Войти в рабочее место</h2>
        <Field label="Почта">
          <input name="email" type="email" autoComplete="username" required />
        </Field>
        <Field label="Пароль">
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </Field>
        <ErrorText error={error} />
        <button className="primary" disabled={busy}>
          {busy ? (
            <Spinner />
          ) : (
            <>
              Продолжить <ArrowRight size={18} />
            </>
          )}
        </button>
        <p className="muted">Доступ к проектам предоставляет ваша команда.</p>
      </form>
    </main>
  );
}

function ProjectForm({
  catalogs,
  close,
  run,
  onCreated,
}: {
  catalogs: Schema["CatalogOut"][];
  close: () => void;
  run: Run;
  onCreated: (id: string) => void;
}) {
  return (
    <Modal title="Новый проект" close={close}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const values = new FormData(e.currentTarget);
          void run(async () => {
            const project = await result(
              client.POST("/api/v1/projects", {
                body: {
                  name: String(values.get("name")),
                  task: String(values.get("task")),
                  sections: [...values.getAll("sections").map(String), ...String(values.get("additional-sections") || "").split(",").map(value=>value.trim()).filter(Boolean)],
                  catalog_id: String(values.get("catalog")) || null,
                },
              }),
            );
            await result(
              client.POST("/api/v1/projects/{project_id}/revisions", {
                params: { path: { project_id: project.id } },
                body: {
                  name: "Исходный комплект",
                  kind: "current",
                  confirmed: false,
                },
              }),
            );
            onCreated(project.id);
            close();
          });
        }}
      >
        <Field label="Название объекта">
          <input
            name="name"
            required
            autoFocus
            placeholder="Например, поликлиника в Люберцах"
          />
        </Field>
        <Field label="Что нужно подготовить">
          <textarea
            name="task"
            rows={3}
            placeholder="Укажите нужные работы и особенности проекта"
          />
        </Field>
        <fieldset>
          <legend>Разделы проекта</legend>
          <label>
            <input type="checkbox" name="sections" value="АР" defaultChecked />{" "}
            АР — архитектурные решения
          </label>
          <label>
            <input type="checkbox" name="sections" value="ЭОМ" defaultChecked />{" "}
            ЭОМ — электрооборудование
          </label>
          <Field label="Другие разделы">
            <input name="additional-sections" placeholder="Например, КМ, SS, ВК" />
          </Field>
        </fieldset>
        <Field label="Нормативная база">
          <select name="catalog">
            <option value="">Выбрать позже</option>
            {catalogs.filter(c => !c.demo_only).map((c) => (
              <option value={c.id} key={c.id}>
                {c.name} · {c.edition}
              </option>
            ))}
          </select>
        </Field>
        <footer>
          <button type="button" onClick={close}>
            Отмена
          </button>
          <button className="primary">Создать проект</button>
        </footer>
      </form>
    </Modal>
  );
}

function Workspace({
  project,
  user,
  run,
  back,
  catalogs,
}: {
  project: Project;
  user: User;
  run: Run;
  back: () => void;
  catalogs: Schema["CatalogOut"][];
}) {
  const qs = new URLSearchParams(location.search);
  const revisions = useQuery({
    queryKey: ["revisions", project.id],
    queryFn: () =>
      result(
        client.GET("/api/v1/projects/{project_id}/revisions", {
          params: { path: { project_id: project.id } },
        }),
      ),
  });
  const [revisionId, setRevisionId] = useState(qs.get("revision") || "");
  const [issueId,setIssueId]=useState(qs.get("issue") || "");
  const [sourceKey,setSourceKey]=useState(qs.get("record") || "");
  const [registerSource,setRegisterSource]=useState((qs.get("issue") || qs.get("tab") === "protocol") ? qs.get("source") || "" : ""),[registerKey,setRegisterKey]=useState(qs.get("record") || "");
  const [sourceComparison,setSourceComparison]=useState<Schema['ProtocolIssue']|null>(null);
  const [tab, setTab] = useState<"works" | "files" | "compare" | "protocol" | "resources" | "history" | "estimate" | "designer" | "svor">((qs.get("issue") || qs.get("tab") === "protocol") ? "protocol" : qs.get("tab") === "estimate" ? "estimate" : "works");
  const [protocolView,setProtocolView]=useState<ProtocolView>(()=>readProtocolView());
  const changeProtocolView=(view:ProtocolView)=>{setProtocolView(view);writeProtocolView(view);};
  const [newRevision, setNewRevision] = useState(false),
    [exportOpen, setExportOpen] = useState(false),
    [renameOpen, setRenameOpen] = useState(false);
  const [sectionNote, setSectionNote] = useState("");
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [exportIds, setExportIds] = useState<string[]>([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const defaultRevision = useQuery({
    queryKey: ["default-revision", project.id, revisions.data?.map(r => r.id).join(",") || ""],
    enabled: !revisionId && !!revisions.data?.length,
    staleTime: 30_000,
    queryFn: () => firstRevisionWithWorks(revisions.data || [], async id => {
      const overview = await cache.fetchQuery({
        queryKey: ["readiness", id],
        staleTime: 30_000,
        queryFn: () => result(client.GET("/api/v1/revisions/{revision_id}/readiness", {
          params: {path: {revision_id: id}},
        })),
      });
      return overview.counts.works;
    }),
  });
  const choosingDefault = !revisionId && !!revisions.data?.length && defaultRevision.isPending;
  const displayedRevisionId = revisionId || defaultRevision.data;
  const revision =
    displayedRevisionId ? revisions.data?.find(r => r.id === displayedRevisionId) : undefined;
  useEffect(() => {
    if (revisionId || !defaultRevision.data || !revisions.data?.some(r => r.id === defaultRevision.data)) return;
    const params = new URLSearchParams(location.search);
    // A manual choice or navigation that happened during the request wins.
    if (params.get("project") !== project.id || params.get("revision")) return;
    setRevisionId(defaultRevision.data);
    params.set("revision", defaultRevision.data);
    history.replaceState(history.state, "", `/?${params}`);
  }, [project.id, revisionId, defaultRevision.data, revisions.data]);
  const [selected, setSelected] = useState(qs.get("work") || ""),
    [externalSource, setExternalSource] = useState((qs.get("issue") || qs.get("tab") === "protocol") ? "" : qs.get("source") || "");
  useEffect(() => {
    const restore = () => {
      const params = new URLSearchParams(location.search), issue = params.get("issue") || "", source = params.get("source") || "", record = params.get("record") || "";
      setRevisionId(params.get("revision") || ""); setIssueId(issue); setSelected(params.get("work") || "");
      const protocol = !!issue || params.get("tab") === "protocol";
      setSourceKey(record); setRegisterKey(record); setExternalSource(protocol ? "" : source); setRegisterSource(protocol ? source : "");
      setSourceComparison(null); setTab(protocol ? "protocol" : "works");
    };
    window.addEventListener("popstate", restore); return () => window.removeEventListener("popstate", restore);
  }, []);
  const sourceRoute = (id: string, key?: string) => {
    const params = new URLSearchParams({project: project.id, revision: revision?.id || revisionId});
    if (issueId) params.set("issue", issueId);
    if (selected) params.set("work", selected);
    if (tab === "protocol") params.set("tab", "protocol");
    if (id) params.set("source", id);
    if (key) params.set("record", key);
    history.pushState(null, "", `/?${params}`);
  };
  const [add, setAdd] = useState(false);
  const current = revision?.id || "";
  const works = useQuery({
    queryKey: ["works", current],
    enabled: !!current,
    queryFn: () =>
      result(
        client.GET("/api/v1/revisions/{revision_id}/works", {
          params: { path: { revision_id: current } },
        }),
      ),
  });
  const docs = useQuery({
    queryKey: ["documents", current],
    enabled: !!current,
    queryFn: () =>
      result(
        client.GET("/api/v1/revisions/{revision_id}/documents", {
          params: { path: { revision_id: current } },
        }),
      ),
  });
  const jobs = useQuery({
    queryKey: ["jobs", current],
    enabled: !!current,
    queryFn: () =>
      result(
        client.GET("/api/v1/revisions/{revision_id}/jobs", {
          params: { path: { revision_id: current } },
        }),
      ),
  });
  const readiness = useQuery({
    queryKey: ["readiness", current],
    enabled: !!current,
    queryFn: () =>
      result(
        client.GET("/api/v1/revisions/{revision_id}/readiness", {
          params: { path: { revision_id: current } },
        }),
      ),
  });
  const failedQueries = [revisions, ...(!revisionId ? [defaultRevision] : []), works, docs, jobs, readiness].filter((query) => query.isError);
  useEffect(() => {
    if (!current) return;
    let stopped = false, retryDelay = 3000;
    let event: EventSource;
    let lastWorkState = "", lastDocumentState = "", lastAnalysisState = "";
    let retry: ReturnType<typeof setTimeout> | undefined;
    const connect = () => {
      if (stopped) return;
      event = new EventSource(`/api/v1/revisions/${current}/events`);
      event.onopen = () => { retryDelay = 3000; };
      event.onerror = () => {
        // HTTP 503 during an API restart can permanently close native EventSource.
        event.close();
        if (!stopped) {
          retry = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
        }
      };
      event.onmessage = async (e) => {
        // A slower GET started before this event must not overwrite the newer status.
        await cache.cancelQueries({ queryKey: ["jobs", current], exact: true });
        if (stopped) return;
        const next = JSON.parse(e.data) as Schema["JobOut"][];
        cache.setQueryData(["jobs", current], next);
        const workState = JSON.stringify(next.filter(j=>!["preview","publish","thumbnails","export"].includes(j.kind)));
        const documentState = JSON.stringify(next.filter(j=>j.kind==="extract"));
        // A started, stopped or finished analysis changes which job pins the next launch.
        const analysisState = JSON.stringify(next.filter(j=>j.kind==="analyze").map(j=>[j.id,j.status]));
        if (workState!==lastWorkState) {
          lastWorkState=workState;
          void cache.invalidateQueries({ queryKey: ["works", current] });
          void cache.invalidateQueries({ queryKey: ["readiness", current] });
        }
        if (analysisState!==lastAnalysisState) {
          lastAnalysisState=analysisState;
          void cache.invalidateQueries({ queryKey: ["analysis-options", current] });
        }
        if (documentState!==lastDocumentState) {
          lastDocumentState=documentState;
          void cache.invalidateQueries({ queryKey: ["documents", current] });
        }
      };
    };
    connect();
    return () => { stopped = true; clearTimeout(retry); event.close(); };
  }, [current]);
  const activeJob = jobs.data?.find((j) =>
    !["preview", "publish", "thumbnails", "export"].includes(j.kind) && ["queued", "running"].includes(j.status),
  );
  const latestAnalysis = jobs.data?.find(j => j.kind === "analyze");
  const picked = works.data?.find((w) => w.id === selected);
  const canEdit = user.role !== "viewer" && project.access_role !== "viewer";
  const catalog = catalogs.find((c) => c.id === project.catalog_id);
  return (
    <>
      {catalogOpen && <Modal title="Нормативная база" close={() => setCatalogOpen(false)}>
        <form onSubmit={e => { e.preventDefault(); const id = String(new FormData(e.currentTarget).get("catalog")) || null;
          void run(async () => { await result(client.PUT("/api/v1/projects/{project_id}", {params: {path: {project_id: project.id}}, body: {name: project.name, task: project.task, sections: project.sections ?? [], catalog_id: id}})); setCatalogOpen(false); }); }}>
          <Field label="База и редакция"><select name="catalog" defaultValue={project.catalog_id || ""}>
            <option value="">Не выбрана</option>{catalogs.filter(c => !c.demo_only || c.id === project.catalog_id).map(c => <option value={c.id} key={c.id}>{c.name} · {c.edition}{c.confirmed ? " · подтверждена" : " · предварительно"}</option>)}
          </select></Field>
          {catalog?.demo_only&&<p className="warning">Выбран учебный каталог. Для подбора норм и цен подключите рабочую нормативную базу.</p>}
          <p className="muted">Выбор базы сохраняет работы и объёмы. После обновления карточек используйте отдельный подбор норм.</p>
          <footer><button className="primary">Сохранить выбор</button></footer>
        </form>
        {catalog && <><p className="muted">Источник: {catalog.source}</p>{user.role === "owner" && <form onSubmit={e => {e.preventDefault(); const reason = String(new FormData(e.currentTarget).get("reason")); void run(async () => {await result(client.PATCH("/api/v1/catalogs/{catalog_id}/confirmation", {params: {path: {catalog_id: catalog.id}}, body: {confirmed: !catalog.confirmed, reason}})); setCatalogOpen(false);});}}>
          <Field label="Основание согласования с клиентом"><textarea name="reason" required /></Field>
          <button>{catalog.confirmed ? "Снять подтверждение базы" : "Подтвердить согласованную базу и редакцию"}</button>
        </form>}</>}
      </Modal>}
      {renameOpen && <Modal title="Название и разделы проекта" close={() => setRenameOpen(false)}>
        <form onSubmit={event => {
          event.preventDefault();
          const values = new FormData(event.currentTarget);
          const name = String(values.get("name") || "").trim();
          const sections = parseSections(String(values.get("sections") || ""));
          const used = sectionsInUse(sections, works.data || []);
          setSectionNote(!sections.length ? "Укажите хотя бы один раздел проекта."
            : used.length ? `В ведомости есть работы разделов: ${used.join(", ")}. Оставьте эти разделы в составе проекта.` : "");
          if (!name || !sections.length || used.length) return;
          void run(async () => {
            if (JSON.stringify(sections) === JSON.stringify(project.sections)) {
              await result(client.PATCH("/api/v1/projects/{project_id}/name", {
                params: { path: { project_id: project.id } },
                body: { name, previous_name: project.name },
              }));
            } else {
              await result(client.PUT("/api/v1/projects/{project_id}", {
                params: { path: { project_id: project.id } },
                body: { name, task: project.task, sections, catalog_id: project.catalog_id },
              }));
            }
            setRenameOpen(false);
          });
        }}>
          <Field label="Название объекта"><input name="name" required maxLength={300} autoFocus defaultValue={project.name} /></Field>
          <Field label="Разделы проекта (через запятую)"><input name="sections" required defaultValue={(project.sections ?? []).join(", ")} placeholder="Например, АР, ЭОМ, КМ, СС" /></Field>
          {sectionNote && <p className="warning" role="alert">{sectionNote}</p>}
          <p className="muted">Изменение не затрагивает исходные файлы, работы, редакции и историю решений. Новый раздел попадёт в следующий анализ; до его завершения итоговая выгрузка покажет, что раздел не проанализирован.</p>
          <footer><button type="button" onClick={() => setRenameOpen(false)}>Отмена</button><button className="primary">Сохранить</button></footer>
        </form>
      </Modal>}
      <section className="project-heading">
        <button className="back quiet" onClick={back}>
          <ArrowLeft size={16} />
          Проекты
        </button>
        <div className="project-title">
          <h1>{project.name}</h1>
          <div className="heading-actions">
            {canEdit && <button className="quiet" onClick={() => { setSectionNote(""); setRenameOpen(true); }}>Название и разделы</button>}
            <button className="quiet" onClick={() => setEstimateOpen(true)} disabled={!current}><CircleDollarSign size={16} />Расход обработки</button>
            <button onClick={() => { setExportIds([]); setExportOpen(true); }} disabled={!current}>
              <Download size={16} />
              Выгрузить
            </button>
          </div>
        </div>
        <div className="project-meta">
          <span>{(project.sections ?? []).join(" + ")}</span>
          <span>
            {catalog
              ? `${catalog.name} · ${catalog.confirmed ? "Редакция базы подтверждена" : "Уточнить редакцию базы"}`
              : "Нормативная база не выбрана"}
          </span>
          {canEdit && <button className="quiet" onClick={() => setCatalogOpen(true)}>Выбрать базу</button>}
          {current ? <span>
            {readiness.data?.counts.approved || 0} из {works.data?.length || 0}{" "}
            работ подтверждено
          </span> : <span>Выбираем комплект проекта…</span>}
        </div>
        <SpartakEstimatePreparation projectId={project.id} canEdit={canEdit} />
        <div className="workspace-nav">
          <nav aria-label="Разделы рабочего места">
            {[
              ["works", "Ведомость"],
              ["files", "Материалы"],
              ["protocol", "Расхождения"],
              ["resources", "КАЦ"],
              ["compare", "Сравнение редакций"],
              ["estimate", "Смета"],
              ["designer", "Сверка проекта"],
              ["svor", "СВОР"],
              ["history", "История"],
            ].map(([id, label]) => (
              <button
                key={id}
                className={tab === id ? "active" : ""}
                onClick={() => setTab(id as typeof tab)}
              >
                {label}
                {id === "files" && (
                  <span className="counter">{docs.data?.length || 0}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="revision-select">
            <Layers3 size={16} />
            <select
              aria-label="Редакция проекта"
              value={current}
              onChange={(e) => {
                setRevisionId(e.target.value); setIssueId(""); setExternalSource(""); setSelected(""); setRegisterSource(""); setSourceComparison(null);
                history.pushState(
                  null,
                  "",
                  `/?project=${project.id}&revision=${e.target.value}`,
                );
              }}
            >
              {!revision && <option value="">Выберите доступный комплект</option>}
              {revisions.data?.map((r) => {
                const parent = r.parent_id
                  ? revisions.data?.find((item) => item.id === r.parent_id)
                  : undefined;
                return (
                  <option key={r.id} value={r.id}>
                    {parent ? `${r.name} ← ${parent.name}` : r.name}
                  </option>
                );
              })}
            </select>
            {canEdit && (
              <button
                className="icon"
                onClick={() => setNewRevision(true)}
                aria-label="Добавить редакцию"
              >
                <Plus size={18} />
              </button>
            )}
            {canEdit && revision && (
              <button
                className="quiet"
                onClick={() => {
                  const name = window.prompt(
                    "Название рабочей копии",
                    `Рабочая копия · ${revision.name}`,
                  );
                  if (!name?.trim()) return;
                  void run(async () => {
                    const copy = await result(
                      client.POST("/api/v1/revisions/{revision_id}/fork", {
                        params: { path: { revision_id: revision.id } },
                        body: { name: name.trim() },
                      }),
                    );
                    setRevisionId(copy.id);
                    setSelected(""); setIssueId(""); setExternalSource(""); setRegisterSource(""); setSourceComparison(null);
                    history.replaceState(null, "", `/?project=${project.id}&revision=${copy.id}`);
                  });
                }}
              >
                Создать рабочую копию
              </button>
            )}
            {canEdit && revision && <button className="quiet" disabled={!!activeJob} onClick={()=>{
              const name=window.prompt(`Удалить комплект «${revision.name}» и его ведомость? История расходов и общие исходные файлы сохранятся. Для подтверждения введите название комплекта.`);
              if(name!==revision.name)return;
              void run(async()=>{
                await result(client.DELETE('/api/v1/revisions/{revision_id}',{params:{path:{revision_id:revision.id}},body:{name}}));
                const next=(revisions.data||[]).find(r=>r.id!==revision.id);
                setRevisionId(next?.id||''); setIssueId(''); setSelected(''); setExternalSource(''); setRegisterSource(''); setTab('works');
                history.replaceState(null,'',`/?project=${project.id}${next?`&revision=${next.id}`:''}`);
              });
            }}>Удалить комплект</button>}
          </div>
        </div>
      </section>
      {typeof latestAnalysis?.details.scope_label === "string" && <p className="notice" role="status">{latestAnalysis.details.scope_label}. Остальные части проекта этим запуском не проверяются.</p>}
      {activeJob && (
        <div className="processing">
          <Spinner />
          <span>{activeJob.message || "Файлы ожидают обработки"}{activeJob.budget && <small className="budget-summary">Расчётный расход ${Number(activeJob.budget.spent_usd).toFixed(2)} из ${Number(activeJob.budget.limit_usd).toFixed(2)} · резерв ${Number(activeJob.budget.reserved_usd).toFixed(2)} · доступно ${Number(activeJob.budget.remaining_usd).toFixed(2)}</small>}{activeJob.kind === "analyze" && activeJob.provider === "codex" && <small className="budget-summary">{providerLabel.codex}. {providerDescription.codex}</small>}</span>
          <progress aria-label="Ход обработки" max={100} value={activeJob.progress || undefined} />
          {activeJob.progress > 0 && <span>{activeJob.progress}%</span>}
          <button
            className="quiet"
            onClick={() =>
              run(() =>
                result(
                  client.POST("/api/v1/jobs/{job_id}/cancel", {
                    params: { path: { job_id: activeJob.id } },
                  }),
                ),
              )
            }
          >
            Отменить
          </button>
        </div>
      )}
      {(!activeJob || latestAnalysis?.status === "failed") && <AnalysisStopNotice job={latestAnalysis} canEdit={canEdit} resume={() => setEstimateOpen(true)} openFiles={() => setTab("files")} stopAuto={() => { if (latestAnalysis) void run(() => result(client.POST("/api/v1/jobs/{job_id}/cancel", { params: { path: { job_id: latestAnalysis.id } } }))); }} />}
      {docs.data?.some((d) => d.status === "failed") && latestAnalysis?.status !== "failed" && (
        <div className="notice" role="alert">
          <span>
            Не удалось прочитать часть файлов. Подробности и повторный запуск — в материалах.
          </span>
          <button className="quiet" onClick={() => setTab("files")}>
            Посмотреть <ArrowRight size={14} />
          </button>
        </div>
      )}
      {!!failedQueries.length && <div className="notice" role="alert"><span>Не удалось загрузить данные проекта. Сохранённые результаты не изменены.<ErrorText error={failedQueries[0].error} /></span><button disabled={failedQueries.some((query) => query.isFetching)} onClick={() => void Promise.all(failedQueries.map((query) => query.refetch()))}>Повторить загрузку</button></div>}
      {revisions.isPending && <div className="empty"><Spinner /><p>Загружаем комплект проекта…</p></div>}
      {choosingDefault && <div className="empty" role="status"><Spinner /><p>Открываем комплект с сохранённой ведомостью…</p></div>}
      {revision && tab === "works" && (works.isPending || docs.isPending) && <div className="empty"><Spinner /><p>Загружаем ведомость и материалы…</p></div>}
      {revision && tab === "works" && works.data !== undefined && docs.data !== undefined && (
        <div
          className={
            "work-area " + (picked || externalSource ? "has-source" : "")
          }
        >
          <section className="work-list">
            <WorkTable
              catalogId={project.catalog_id ?? null}
              rows={works.data || []}
              selected={selected}
              pick={(id) => {
                setSelected(id);
                setExternalSource("");
              }}
              canEdit={canEdit}
              run={run}
              revision={revision}
              add={() => setAdd(true)}
              analyze={() => setEstimateOpen(true)}
              isProcessing={!!activeJob || jobs.data === undefined || jobs.isError}
              processingJob={activeJob}
              openFiles={() => setTab("files")}
              hasFiles={!!docs.data?.length}
              exportSelection={(ids) => { setExportIds(ids); setExportOpen(true); }}
              sections={project.sections ?? []}
            />
          </section>
          {(picked || externalSource) && (
            <aside className="source-panel" key={picked?.id || externalSource}>
              {picked ? (
                <WorkInspector
                  key={picked.id}
                  work={picked}
                  project={project}
                  revisionId={revision.id}
                  run={run}
                  canEdit={canEdit}
                  close={() => setSelected("")}
                />
              ) : (
                <SourceView
                  id={externalSource} recordKey={sourceKey}
                  revisionId={revision?.id}
                  onOpenSource={(id, key) => { setExternalSource(id); setSourceKey(key || ""); setSelected(""); sourceRoute(id, key); }}
                  close={() => { setExternalSource(""); sourceRoute(""); }}
                />
              )}
            </aside>
          )}
        </div>
      )}
      {revision && tab === "files" && (docs.isPending || jobs.isPending) && <div className="empty"><Spinner /><p>Загружаем материалы…</p></div>}
      {revision && tab === "files" && docs.data !== undefined && jobs.data !== undefined && (
        <Materials
          revision={revision}
          documents={docs.data || []}
          jobs={jobs.data || []}
          run={run}
          canEdit={canEdit}
          selectSource={(id) => {
            setExternalSource(id);
            setSelected("");
            setTab("works");
          }}
        />
      )}
      {estimateOpen && <Modal title="Запуск анализа и расход обработки" close={() => setEstimateOpen(false)} wide>
        <AnalysisEstimate revisionId={current} processing={!!activeJob || !canEdit || jobs.data === undefined || jobs.isError} close={() => setEstimateOpen(false)} start={body => run(async () => {
          await result(client.POST("/api/v1/revisions/{revision_id}/analyze", { params: { path: { revision_id: current } }, body }));
          setEstimateOpen(false);
        })} />
      </Modal>}
      {revision && (tab === "protocol" || tab === "resources") && <ReviewRegister focusedIssueId={issueId} focusedWorkId={qs.get("tab") === "protocol" ? selected : ""} clearFocus={()=>{setIssueId("");setSelected("");history.replaceState(null,"",`/?project=${project.id}&revision=${current}`);}} canEdit={canEdit} revisionId={revision.id} mode={tab} availableWorks={works.data ?? []} protocolView={protocolView} onProtocolViewChange={changeProtocolView} openWork={id=>{setSelected(id);setTab("works");}} openSource={(id,key)=>{setRegisterSource(id);setRegisterKey(key||"");sourceRoute(id,key);}} openComparison={setSourceComparison} />}
      {revision && tab === "estimate" && <EstimateComparison key={revision.id} projectId={project.id} revisionId={revision.id} canEdit={canEdit} availableWorks={works.data ?? []} openWork={id=>{setSelected(id);setTab("works");}} openSource={(id,key)=>{setRegisterSource(id);setRegisterKey(key||"");sourceRoute(id,key);}} />}
      {revision && tab === "designer" && <DesignerReview key={revision.id} revisionId={revision.id} canEdit={canEdit} availableWorks={works.data ?? []} openSource={(id,key)=>{setRegisterSource(id);setRegisterKey(key||"");sourceRoute(id,key);}} />}
      {revision && tab === "svor" && <SvorPanel key={revision.id} revisionId={revision.id} canEdit={canEdit} sections={project.sections ?? []} />}
      {revision && tab==='history' && <RevisionHistory key={revision.id} revisionId={revision.id} openWork={id=>{setSelected(id);setTab('works');}}/>}
      {sourceComparison&&revision&&<SourceComparison revisionId={revision.id} issue={sourceComparison} close={()=>setSourceComparison(null)}/>}
      {registerSource && <Modal title="Источник замечания" fullScreen close={()=>{setRegisterSource("");sourceRoute("");}}><SourceView id={registerSource} recordKey={registerKey} marker={registerKey ? undefined : {tone:'amber',label:'Замечание',precision:'unresolved'}} revisionId={revision?.id} onOpenSource={(id,key)=>{setRegisterSource(id);setRegisterKey(key||"");sourceRoute(id,key);}} expanded /></Modal>}
      {tab === "compare" && (
        <Comparison
          revisions={revisions.data || []}
          run={run}
          canEdit={canEdit}
        />
      )}
      {!revision && revisions.isSuccess && (Boolean(revisionId) || revisions.data.length === 0) && (
        <div className="empty">
          <Layers3 size={32} />
          <h2>{revisionId ? "Этот комплект удалён или недоступен" : "Добавьте первый комплект"}</h2>
          {revisionId && <p>Выберите нужный комплект в списке выше. Другой объект автоматически не открывается.</p>}
          {canEdit && <button onClick={() => setNewRevision(true)}>Создать редакцию</button>}
        </div>
      )}
      {newRevision && (
        <Modal title="Новая редакция" close={() => setNewRevision(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              void run(async () => {
                const r = await result(
                  client.POST("/api/v1/projects/{project_id}/revisions", {
                    params: { path: { project_id: project.id } },
                    body: {
                      name: String(data.get("name")),
                      kind: String(data.get("kind")) as
                        "current" | "before" | "after",
                      confirmed: false,
                    },
                  }),
                );
                setRevisionId(r.id);
                setSelected(""); setIssueId(""); setExternalSource(""); setRegisterSource(""); setSourceComparison(null);
                history.replaceState(null, "", `/?project=${project.id}&revision=${r.id}`);
                setTab("files");
                setNewRevision(false);
              });
            }}
          >
            <Field label="Название">
              <input
                name="name"
                required
                autoFocus
                placeholder="Например, корректировка от 12 сентября"
              />
            </Field>
            <Field label="Назначение комплекта">
              <select name="kind">
                <option value="current">Текущая редакция</option>
                <option value="before">Было</option>
                <option value="after">Стало</option>
              </select>
            </Field>
            <footer>
              <button className="primary">Добавить редакцию</button>
            </footer>
          </form>
        </Modal>
      )}
      {add && revision && (
        <AddWork
          sections={project.sections ?? []}
          rows={works.data || []}
          revision={revision}
          documents={docs.data || []}
          run={run}
          close={() => setAdd(false)}
          created={setSelected}
        />
      )}
      {exportOpen && revision && (
        <Export
          jobs={jobs.data ?? []}
          revision={revision}
          revisions={revisions.data || []}
          readiness={readiness.data}
          rows={works.data || []}
          selectedIds={exportIds}
          close={() => setExportOpen(false)}
        />
      )}
    </>
  );
}

const column = createColumnHelper<Work>();

function useDocumentDelivery(revisionId: string) {
  return useQuery({queryKey: ["document-delivery", revisionId], refetchInterval: 5000,
    queryFn: () => result(client.GET("/api/v1/revisions/{revision_id}/document-delivery", {params: {path: {revision_id: revisionId}}}))});
}

function DocumentOptions({plan}: {plan?: DocumentDelivery}) {
  return <><option value="">Все документы</option>{plan?.documents.map(document =>
    <option key={document.document_id} value={document.document_id}>{document.path} · {document.role === "supporting" ? "справочный" : `работ: ${document.work_ids.length}`}</option>)}
    {!!plan?.unassigned_ids.length && <option value="unassigned">Принадлежность PDF не определена · работ: {plan.unassigned_ids.length}</option>}</>;
}

function WorkTable({
  catalogId,
  rows,
  selected,
  pick,
  canEdit,
  run,
  revision,
  add,
  analyze,
  isProcessing,
  processingJob,
  openFiles,
  hasFiles,
  exportSelection,
  sections,
}: {
  sections: string[];
  catalogId: string | null;
  rows: Work[];
  selected: string;
  pick: (id: string) => void;
  canEdit: boolean;
  run: Run;
  revision: Revision;
  add: () => void;
  analyze: () => void;
  isProcessing: boolean;
  processingJob?: Schema["JobOut"];
  openFiles: () => void;
  hasFiles: boolean;
  exportSelection: (ids: string[]) => void;
}) {
  const [showSummary,setShowSummary]=useState(false);
  const [showSourceStructure,setShowSourceStructure]=useState(false);
  const [documentId, setDocumentId] = useState("");
  const delivery = useDocumentDelivery(revision.id);
  const [query, setQuery] = useState(""),
    [section, setSection] = useState(""),
    [building, setBuilding] = useState(""),
    [category, setCategory] = useState(""),
    [type, setType] = useState(""),
    [status, setStatus] = useState("");
  const [checked, setChecked] = useState<Record<string, boolean>>({}),
    [merge, setMerge] = useState(false);
  const [complex, setComplex] = useState<Work[] | null>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const filtered = useMemo(
    () =>
      documentWorks(rows, delivery.data, documentId).filter(
        (w) =>
          (!section || w.discipline === section) &&
          (!building || (w.building || "__unassigned") === building) &&
          (!category || w.category === category) &&
          (!type || w.group === type) &&
          (!status || w.status === status) &&
          `${w.description} ${w.group} ${w.category} ${w.building}`
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase()),
      ).sort((a,b)=>[a.building,a.discipline,a.category,a.group].join("/").localeCompare([b.building,b.discipline,b.category,b.group].join("/"),"ru",{numeric:true}) || (a.sequence ?? 100000)-(b.sequence ?? 100000)),
    [rows, query, section, building, category, type, status, delivery.data, documentId],
  );
  const columns = useMemo(
    () => [
      column.display({
        id: "select",
        header: "",
        cell: (cell) =>
          canEdit && (
            <input
              aria-label={`Выбрать: ${cell.row.original.description}`}
              type="checkbox"
              checked={!!checked[cell.row.original.id]}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                setChecked((v) => ({
                  ...v,
                  [cell.row.original.id]: e.target.checked,
                }))
              }
            />
          ),
      }),
      column.accessor("description", {
        header: "Работа / основание",
        cell: (cell) => (
          <div className="work-description">
            <strong>{cell.getValue()}</strong>
            <span>
              {[cell.row.original.group, cell.row.original.mount]
                .filter(Boolean)
                .join(" · ")}
              {cell.row.original.source_ids.length
                ? ` · ${cell.row.original.source_ids.length} ист.`
                : " · нет основания"}
            </span>
            {calculationGuide(cell.row.original) && <span className="calculation-guide-label">Есть предварительный расчёт · откройте работу → Расчёт</span>}
          </div>
        ),
      }),
      column.accessor("quantity", {
        header: "Объём",
        cell: (cell) => (
          <span
            className={"quantity " + (cell.getValue() == null ? "unknown" : "")}
          >
            {amount(cell.getValue())}
            <small>{cell.row.original.unit}</small>
            {"quantity" in cell.row.original.manual && (
              <i title="Уточнён специалистом">✎</i>
            )}
          </span>
        ),
      }),
      column.display({
        id: "norms",
        header: "Нормы",
        cell: (cell) => (
          <span className="norm-code">
            {cell.row.original.norms.length ? (
              cell.row.original.norms.map((n) => normCode(n.norm.code)).join(" · ")
            ) : (
              <span className="muted">Подобрать</span>
            )}
          </span>
        ),
      }),
      column.accessor("status", {
        header: "Проверка",
        cell: (cell) => (
          <span className={`status ${cell.getValue()}`}>
            {cell.getValue() === "approved" && <Check size={12} />}
            {statusLabel[cell.getValue()]}
          </span>
        ),
      }),
    ],
    [canEdit, checked],
  );
  const table = useReactTable({
    data: filtered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });
  const tableRows = table.getRowModel().rows;
  const virtual = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scroll.current,
    estimateSize: () => 91,
    overscan: 8,
  });
  const selection = rows.filter((row) => checked[row.id]);
  return (
    <>
      <div className="table-toolbar">
        {!!delivery.data?.documents.length && <select aria-label="Исходный PDF в ведомости" value={documentId} onChange={e => {
          setDocumentId(e.target.value); setSection(""); setBuilding(""); setCategory(""); setType(""); setChecked({});
        }}><DocumentOptions plan={delivery.data}/></select>}
        <div className="search">
          <Search size={16} />
          <input
            aria-label="Поиск работ"
            placeholder="Найти работу, тип или марку"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          aria-label="Раздел"
          value={section}
          onChange={(e) => { setSection(e.target.value); setBuilding(""); setCategory(""); setType(""); }}
        >
          <option value="">Все разделы</option>
          {sectionOptions(sections, rows).map(value => <option key={value}>{value}</option>)}
        </select>
        {(building || rows.some(w => w.building)) && <select aria-label="Здание в ведомости" value={building} onChange={e => setBuilding(e.target.value)}>
          <option value="">Все здания</option>
          {[...new Set(rows.filter(w => !section || w.discipline === section).map(w => w.building || "__unassigned"))]
            .sort((a, b) => a.localeCompare(b, "ru", {numeric: true}))
            .map(value => <option key={value} value={value}>{value === "__unassigned" ? "Здание не указано" : value}</option>)}
        </select>}
        <select aria-label="Подраздел" value={category} onChange={e => { setCategory(e.target.value); setType(""); }}>
          <option value="">Все подразделы</option>
          {[...new Set(rows.filter(w => !section || w.discipline === section).map(w => w.category))].sort().map(value => <option key={value}>{value}</option>)}
        </select>
        {category && <select aria-label="Тип или марка" value={type} onChange={e => setType(e.target.value)}>
          <option value="">Все типы / марки</option>
          {[...new Set(rows.filter(w => (!section || w.discipline === section) && w.category === category).map(w => w.group).filter(Boolean))].sort((a,b) => a.localeCompare(b, "ru", {numeric:true})).map(value => <option key={value}>{value}</option>)}
        </select>}
        <select
          aria-label="Состояние проверки"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Все работы</option>
          {Object.entries(statusLabel).map(([value, label]) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </select>
        {canEdit && (
          <button
            className="icon"
            onClick={add}
            title="Добавить работу"
            aria-label="Добавить работу"
          >
            <Plus size={19} />
          </button>
        )}
      </div>
      {documentId && <p className="notice" role="status">{documentNote(delivery.data, documentId)}</p>}
      {!documentId && !!delivery.data?.unassigned_ids.length && <p className="notice">Основной PDF не определён для работ: {delivery.data.unassigned_ids.length}. Они сохранены в общем своде один раз. <button className="quiet" onClick={() => setDocumentId("unassigned")}>Показать</button></p>}
      {delivery.error && <p role="alert" className="error">Не удалось загрузить принадлежность работ документам: {String(delivery.error)}</p>}
      <div className="table-summary">
        <span>
          Работ: {filtered.length}
          {selection.length > 0 && ` · выбрано ${selection.length}`}
        </span>
        <div>
          <button className="quiet" disabled={!rows.length || !delivery.data} onClick={()=>setShowSourceStructure(true)}>Связь с исходными PDF</button>
          <button className="quiet" onClick={()=>setShowSummary(v=>!v)}>{showSummary ? "Рабочая ведомость" : "Свод по типам"}</button>
          <button className="quiet" onClick={() => exportSelection((selection.length ? selection : filtered).map(w => w.id))} disabled={!filtered.length && !selection.length}><Download size={14} />{selection.length ? "Выгрузить выбранные" : "Выгрузить этот вид"}</button>
          {selection.length > 1 && <button className="quiet" onClick={() => setComplex([...selection])}>Комплексная норма</button>}
          {selection.length > 1 && (
            <button className="quiet" onClick={() => setMerge(true)}>
              Объединить выбранные
            </button>
          )}
          {canEdit && (
            <button
              className="quiet"
              disabled={isProcessing || !hasFiles}
              onClick={analyze}
            >
              <RefreshCw size={14} />
              {rows.length ? "Повторить анализ" : "Составить ведомость"}
            </button>
          )}
        </div>
      </div>
      {showSourceStructure && <Modal title="Связь работ с исходными PDF" wide close={()=>setShowSourceStructure(false)}>
        <SourceStructure rows={filtered} revisionId={revision.id} plan={delivery.data} pick={id=>{setShowSourceStructure(false);pick(id);}}/>
      </Modal>}
      {showSummary && <TypeSummaryPanel revisionId={revision.id} building={building} section={section} category={category} type={type} pick={id=>{pick(id);}} />}
      <div
        hidden={showSummary}
        role="table"
        aria-label="Ведомость объёмов работ"
        className="work-table"
      >
        <div role="row" className="table-head">
          {table.getHeaderGroups()[0].headers.map((h) => (
            <div role="columnheader" key={h.id}>
              {flexRender(h.column.columnDef.header, h.getContext())}
            </div>
          ))}
        </div>
        <div className="table-scroll" ref={scroll}>
          <div style={{ height: virtual.getTotalSize(), position: "relative" }}>
            {virtual.getVirtualItems().map((item) => {
              const row = tableRows[item.index],
                work = row.original;
              const group = [work.building, work.discipline, work.category, work.group]
                .filter(Boolean)
                .join(" / ");
              const rooms = placementLabels(filtered.filter(w=>w.building===work.building && w.discipline===work.discipline && w.category===work.category && w.group===work.group).flatMap(w=>w.placements || []));
              return (
                <div
                  key={row.id}
                  ref={virtual.measureElement}
                  data-index={item.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${item.start}px)`,
                  }}
                >
                  <div className="row-group">
                    {item.index === 0 ||
                    [
                      tableRows[item.index - 1].original.building,
                      tableRows[item.index - 1].original.discipline,
                      tableRows[item.index - 1].original.category,
                      tableRows[item.index - 1].original.group,
                    ]
                      .filter(Boolean)
                      .join(" / ") !== group
                      ? <span>{group}<small className="group-rooms">{rooms.length ? `Помещения / места: ${rooms.join("; ")}` : "Расшифровка по помещениям требует проверки"}</small></span>
                      : ""}
                  </div>
                  <div
                    className={`table-row ${selected === row.id ? "selected" : ""} ${work.status === "rejected" ? "rejected-row" : ""}`}
                    role="row"
                    aria-selected={selected === row.id}
                    tabIndex={0}
                    onClick={() => pick(row.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") pick(row.id);
                      if (["ArrowDown", "ArrowUp"].includes(e.key)) {
                        e.preventDefault();
                        const next =
                          item.index + (e.key === "ArrowDown" ? 1 : -1);
                        if (tableRows[next]) {
                          pick(tableRows[next].id);
                          virtual.scrollToIndex(next);
                        }
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <div role="cell" key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {!filtered.length && (
            <div className="empty">
              {!rows.length && processingJob ? <Spinner /> : <FileText size={32} />}
              <h2>
                {rows.length
                  ? "Ничего не найдено"
                  : processingJob
                    ? processingJob.kind === "analyze" ? "Составляем ведомость" : "Читаем проектные материалы"
                  : hasFiles
                    ? "Исходные данные готовы к работе"
                    : "Загрузите проектные материалы"}
              </h2>
              <p>
                {rows.length
                  ? "Измените поиск или фильтр."
                  : processingJob
                    ? processingJob.kind === "analyze"
                      ? "Проверяем источники. Работы появятся по мере завершения подразделов. Можно закрыть страницу — обработка продолжится на сервере."
                      : "Обработка идёт на сервере. После чтения файлов можно проверить состав комплекта и запустить анализ."
                  : hasFiles
                    ? "Запустите анализ, чтобы получить работы с привязкой к чертежам. Каждую строку можно проверить и исправить."
                    : "Чертежи DWG, DXF, PDF, ведомости или архивы с вложенными папками."}
              </p>
              {!rows.length && !processingJob && (
                <button
                  className="primary"
                  disabled={isProcessing}
                  onClick={hasFiles ? analyze : openFiles}
                >
                  {hasFiles ? "Составить ведомость" : "Добавить материалы"}
                  <ArrowRight size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {complex && <Modal title="Норма для нескольких работ" wide close={() => setComplex(null)}><ComplexNorm works={complex} catalogId={catalogId} submit={action => run(async () => { await action(); setComplex(null); setChecked({}); })} /></Modal>}
      {merge && (
        <Modal
          title={`Объединить ${selection.length} работы`}
          close={() => setMerge(false)}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const values = new FormData(e.currentTarget);
              void run(async () => {
                const row = await result(
                  client.POST("/api/v1/revisions/{revision_id}/merge", {
                    params: { path: { revision_id: revision.id } },
                    body: {
                      versions: Object.fromEntries(
                        selection.map((w) => [w.id, w.version]),
                      ),
                      description: String(values.get("description")),
                      reason: String(values.get("reason")),
                    },
                  }),
                );
                setChecked({});
                pick(row.id);
                setMerge(false);
              });
            }}
          >
            <ul className="preview-list">
              {selection.map((w) => (
                <li key={w.id}>
                  {w.description}
                  <b>
                    {amount(w.quantity)} {w.unit}
                  </b>
                </li>
              ))}
            </ul>
            <Field label="Название объединённой работы">
              <input
                name="description"
                required
                defaultValue={selection[0]?.description}
              />
            </Field>
            <Field label="Основание объединения">
              <textarea name="reason" required />
            </Field>
            <p className="muted">
              Система проверит единицы и повторные измерения. Набор норм
              потребуется проверить заново.
            </p>
            <footer>
              <button className="primary">Объединить</button>
            </footer>
          </form>
        </Modal>
      )}
    </>
  );
}

// Scale, focus and position belong to the source being read, not to one viewer: the full-screen
// viewer continues where the inline one was and hands the same state back on close.
type SharedView={zoom:number;setZoom:React.Dispatch<React.SetStateAction<number>>;focusTick:number;setFocusTick:React.Dispatch<React.SetStateAction<number>>;fitRequest:number;setFitRequest:React.Dispatch<React.SetStateAction<number>>;cadFocus:CadFocus;setCadFocus:React.Dispatch<React.SetStateAction<CadFocus>>;memory:ViewMemory;origin:PagerOrigin|null;setOrigin:React.Dispatch<React.SetStateAction<PagerOrigin|null>>;pagerTarget:{current:string}};
// The sheet the operator came from before turning PDF pages.
type PagerOrigin={id:string;key:string;sheet:string};

function SourceView({ id, close, facts = [], expanded = false, recordKey = "", recordKeys = [], marker, pageSelection, revisionId, onOpenSource, sharedView }: { marker?:{tone:'amber'|'blue';label:string;precision?:'exact'|'approximate'|'unresolved'};recordKeys?:string[];pageSelection?:{value:string;select:(id:string)=>void};recordKey?:string; id: string; close?: () => void; facts?: Schema["FactOut"][]; expanded?: boolean; revisionId?: string; onOpenSource?: (id:string,key?:string)=>void; sharedView?:SharedView }) {
  const [text, setText] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDocument, setSearchDocument] = useState("");
  const [searchSheet, setSearchSheet] = useState("");
  const sourceSearch = useQuery({
    queryKey: ["source-search", revisionId, search, searchDocument, searchSheet],
    enabled: !!revisionId && search.trim().length > 1,
    staleTime: 30_000,
    queryFn: () => result(client.GET("/api/v1/revisions/{revision_id}/sources/search", {
      params: { path: { revision_id: revisionId! }, query: { q: search.trim(), document_id: searchDocument.trim() || undefined, sheet: searchSheet.trim() || undefined } },
    })),
  });
  const anchorKey=recordKey || String(facts.find(f=>f.fragment_id===id && f.context.record_key)?.context.record_key || '');
  const anchorKeys=[...new Set([anchorKey,...recordKeys].filter(Boolean))].slice(0,100);
  const source = useQuery({
    queryKey: ["source-view", id, anchorKeys.join('|'), text],
    staleTime: 300_000,
    placeholderData: previous => previous?.id === id ? previous : undefined,
    enabled: !!id,
    queryFn: () =>
      result(
        client.GET("/api/v1/sources/{source_id}/view", {
          params: { path: { source_id: id }, query: {record_keys:anchorKeys.length?anchorKeys:undefined, include_text:text} },
        }),
      ),
  });
  const preview = useQuery({
    queryKey: ["preview", id, "vector", expanded],
    enabled: !!source.data && source.data.metadata_json.kind !== "pdf" && source.data.metadata_json.kind !== "cad",
    queryFn: () => result(client.GET("/api/v1/sources/{source_id}/preview/status", {params: {path: {source_id: id}, query: {vector: true, full_sheet: expanded}}})),
    refetchInterval: query => ["queued", "running"].includes(query.state.data?.status || "") ? 2000 : false,
  });
  // A located value opens zoomed on its row; «Лист целиком» releases the focus, «К отметке» restores it.
  const [ownFocusTick, setOwnFocusTick] = useState(1);
  const [localZoom, setLocalZoom] = useState(1),
    [failed, setFailed] = useState(false),
    [fullScreen, setFullScreen] = useState(false),
    [showFacts, setShowFacts] = useState(false);
  const [selectedPage,setSelectedPage]=useState('');
  const [ownFitRequest,setOwnFitRequest]=useState(0);
  const [ownCadFocus,setOwnCadFocus]=useState<CadFocus>({pageId:'',reset:0,request:0});
  const ownMemory=useRef<ViewCenter|null>(null);
  const [ownOrigin,setOwnOrigin]=useState<PagerOrigin|null>(null);
  const ownPagerTarget=useRef('');
  const view:SharedView=sharedView ?? {origin:ownOrigin,setOrigin:setOwnOrigin,pagerTarget:ownPagerTarget,zoom:localZoom,setZoom:setLocalZoom,focusTick:ownFocusTick,setFocusTick:setOwnFocusTick,fitRequest:ownFitRequest,setFitRequest:setOwnFitRequest,cadFocus:ownCadFocus,setCadFocus:setOwnCadFocus,memory:ownMemory};
  const {zoom,setZoom,focusTick,setFocusTick,fitRequest,setFitRequest}=view;
  // Only the owner resets the view, and only for another source: mounting the full-screen viewer is not a new source.
  useEffect(() => { if(!sharedView) setFocusTick(1); }, [id, recordKey]);
  // A source opened in any other way than by turning pages is a new starting point.
  useEffect(() => { if(!sharedView && view.pagerTarget.current!==id) view.setOrigin(null); }, [id]);
  const cadSelection=pageSelection || {value:selectedPage,select:setSelectedPage};
  useEffect(()=>{if(!pageSelection)setSelectedPage('');},[id,recordKey]);
  useEffect(() => {
    setFailed(false);
    if(!sharedView) setZoom(1);
  }, [id]);
  const sheet = source.data?.sheet || "";
  const numberedSheet = sheet.match(/^(\d+(?:\.\d+)?)\s+(.+)$/u);
  const sheetLabel = source.data?.metadata_json.kind === "pdf"
    ? sourcePageLabel(source.data.document_title, String(Number(source.data.metadata_json.page) + 1), source.data.drawing_sheet)
    : numberedSheet ? `Лист ${numberedSheet[1]}` : sourcePageLabel("", sheet);
  const sheetTitle = numberedSheet?.[2] || "";
  const sourceFacts = facts.filter(f => f.fragment_id === id);
  const pdfMarkers = pdfRegions(source.data?.metadata_json || {}, anchorKey, anchorKeys);
  const markerPrecision = marker?.precision || (sourceFacts.some(f=>f.context.record_key===anchorKey&&f.context.record_key_precision==='approximate')?'approximate':'exact');
  const pdfNotice = source.data?.metadata_json.kind === "pdf" && !text ? pdfMarkerNotice({precision:markerPrecision, anchor: anchorKey, highlighted: !!pdfMarkers.highlight, unlocated: pdfMarkers.unlocated,
    missing: Array.isArray(source.data.metadata_json.missing_record_keys) ? source.data.metadata_json.missing_record_keys as string[] : []}) : undefined;
  const isPdf = source.data?.metadata_json.kind === "pdf";
  const pdfPainted = useViewTiming(isPdf ? source.data?.document_id : undefined, isPdf ? `pdf/${Number(source.data?.metadata_json.page)}` : "", false);
  const turnPage = (target: string) => { if (!view.origin) view.setOrigin({ id, key: recordKey, sheet }); view.pagerTarget.current = target; onOpenSource?.(target); };
  const turnBack = () => { const origin = view.origin; if (!origin) return; view.pagerTarget.current = origin.id; view.setOrigin(null); onOpenSource?.(origin.id, origin.key || undefined); };
  const openLabel = /спецификаци/iu.test(sheet) ? "Открыть спецификацию на весь экран" : "Открыть источник на весь экран";
  return (
    <>
    <div className="source-view">
      <div className="source-toolbar">
        <div className="source-document-title">
          <FileText size={15} />
          <span><strong>{expanded && sheet==='Model' ? 'Чертёж' : sheetLabel}</strong>{expanded ? <span>{[source.data?.revision_name, source.data?.document_title].filter(Boolean).join(" · ")}</span> : sheetTitle && <span>{sheetTitle}</span>}</span>
        </div>
        <div className="source-tools">
          {expanded && sourceFacts.length>0 && <button className="quiet" onClick={()=>setShowFacts(v=>!v)}>{showFacts ? "Скрыть основание" : "Данные основания"}</button>}
          <button className="quiet" onClick={() => setText((v) => !v)}>
            {text ? "Чертёж" : "Исходный текст"}
          </button>
          {!text && (
            <>
              {(expanded || source.data?.metadata_json.kind === 'cad' || pdfMarkers.highlight) && <button className="quiet" onClick={() => {setZoom(1);setFitRequest(v=>v+1);setFocusTick(0);}}>Лист целиком</button>}
              {pdfMarkers.highlight && !focusTick && <button className="quiet" onClick={() => {setZoom(1);setFocusTick(Date.now());}}>К отметке</button>}
              <button
                className="icon"
                aria-label="Уменьшить"
                onClick={() => setZoom((v) => Math.max(0.5, v / 1.5))}
              >
                <ZoomOut size={16} />
              </button>
              <button
                className="icon"
                aria-label="Увеличить"
                onClick={() => setZoom((v) => Math.min(source.data?.metadata_json.kind === "cad" ? 128 : 4, v * 1.5))}
              >
                <ZoomIn size={16} />
              </button>
            </>
          )}
          {close && (
            <button
              className="icon"
              aria-label="Закрыть источник"
              onClick={close}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
      {revisionId && <details className="source-search">
        <summary>Найти фрагмент в проекте</summary>
        <form onSubmit={event => { event.preventDefault(); setSearch((event.currentTarget.elements.namedItem("source-query") as HTMLInputElement).value); }}>
          <input name="source-query" aria-label="Поиск по источникам" placeholder="Марка, число или текст" />
          <input aria-label="Фильтр документа" value={searchDocument} onChange={event => setSearchDocument(event.target.value)} placeholder="Документ (необязательно)" />
          <input aria-label="Фильтр листа" value={searchSheet} onChange={event => setSearchSheet(event.target.value)} placeholder="Лист (необязательно)" />
          <button type="submit">Найти</button>
        </form>
        {sourceSearch.isFetching && <p className="muted">Ищем в текстовых слоях…</p>}
        {sourceSearch.data?.total != null && sourceSearch.data.total > 0 && <p className="muted">Найдено: {sourceSearch.data.total}. Способ совпадения: {sourceSearch.data.match_kind === "exact" ? "точное" : sourceSearch.data.match_kind === "phrase" ? "фраза" : sourceSearch.data.match_kind === "full_text" ? "полнотекстовое" : sourceSearch.data.match_kind === "weak" ? "приблизительное" : "по документу"}.</p>}
        {sourceSearch.data?.total === 0 && <p className="muted">Совпадений не найдено.</p>}
        {sourceSearch.data?.sources.map(item => <div className="source-search-result" key={item.id}>
          <span><strong>{item.document} · {sourcePageLabel(item.document, item.sheet)}</strong><small>{item.excerpt}</small></span>
          <button type="button" onClick={() => onOpenSource?.(item.id, item.record_key || undefined)}>Открыть</button>
        </div>)}
      </details>}
      {!expanded && source.data && <button className="source-expand" onClick={() => setFullScreen(true)}><Maximize2 size={16} />{openLabel}</button>}
      <ErrorText error={source.error} />
      {marker && !anchorKeys.length && <p className="cad-page-warning">Точное место значения не установлено. Доступен полный источник для проверки. Для не найденного на плане элемента область выделения не создаётся.</p>}
      {pdfNotice && <p className={pdfNotice.kind === "exact" ? `cad-marker-legend ${marker?.tone || "amber"} exact` : "cad-page-warning"}>{pdfNotice.kind === "exact" && <span aria-hidden="true" />}{pdfNotice.text}</p>}
      {Array.isArray(source.data?.metadata_json.missing_record_keys)&&source.data.metadata_json.missing_record_keys.length>0&&<p className="cad-page-warning">Адрес значения в этом источнике не найден. Проверьте основание на полном листе.</p>}
      {sourceFacts.length > 0 && (!expanded || showFacts) && <div className="source-measurements">
        <dl>{sourceFacts.map(f => {
          const parts = f.label.split(/\s+[—–]\s+/u);
          const subject = parts.length > 1 ? parts[0] : "";
          const measurement = parts.length > 1 ? parts.slice(1).join(" — ") : f.label;
          const label = measurement === "площадь единицы" && /^ОК[-\s]/iu.test(subject) ? "Площадь одного окна" : measurement.charAt(0).toUpperCase() + measurement.slice(1);
          return <div key={f.id}><dt>{label}{subject && <small>{subject}</small>}</dt><dd>{f.value === null ? f.quote : <><strong>{amount(f.value)}</strong> <span>{f.unit.replace(/м2/g, "м²").replace(/м3/g, "м³")}</span></>}</dd></div>;
        })}</dl>
        <details className="source-addresses"><summary>Точные значения и адреса в чертеже</summary>{sourceFacts.map(f => <blockquote key={f.id}><p>{f.quote}</p><cite>{f.label}{f.context.record_key ? ` · ${String(f.context.record_key)}` : ""}</cite></blockquote>)}</details>
      </div>}
      {isPdf && !text && onOpenSource && source.data && <PdfPager documentId={source.data.document_id} sourceId={id} open={turnPage} origin={view.origin} back={turnBack} />}
      {text ? (
        <pre className="source-text">
          {source.isFetching && !source.data?.text ? "Загружаем текст…" : source.data?.text ||
            "В документе нет текстового слоя. Используйте изображение."}
        </pre>
      ) : source.data?.metadata_json.kind === "cad" ? <CadPages source={source.data} facts={facts} recordKey={recordKey} marker={marker} recordKeys={recordKeys} zoom={zoom} fitRequest={fitRequest} resetZoom={()=>setZoom(1)} selected={cadSelection.value} select={cadSelection.select} focusState={{value:view.cadFocus,set:view.setCadFocus}} viewMemory={view.memory} active={!fullScreen}/> : (
        <div className="drawing-scroll">
          {failed || preview.data?.status === "failed" || preview.data?.status === "cancelled" ? (
            <div className="empty">
              <p>{preview.data?.message || "Предпросмотр не удалось построить."}</p>
              <button onClick={() => { setFailed(false); void result(client.GET("/api/v1/sources/{source_id}/preview/status", {params: {path: {source_id: id}, query: {retry: true, vector: true, full_sheet: expanded}}})).then(data => cache.setQueryData(["preview", id, "vector", expanded], data)); }}>Повторить подготовку</button>
              <button onClick={() => setText(true)}>
                Прочитать извлечённый текст
              </button>
            </div>
          ) : source.data?.metadata_json.kind === "pdf" ? (
            <PdfSource url={`/api/v1/documents/${source.data.document_id}/download`} page={Number(source.data.metadata_json.page)} zoom={zoom} fitPage={expanded && !(focusTick && pdfMarkers.highlight)} label={sheetLabel} highlight={pdfMarkers.highlight} focusArea={pdfMarkers.focus} focusRequest={pdfMarkers.highlight ? focusTick : 0} focusRegion precision={markerPrecision==='approximate'?'approximate':'exact'} markers={pdfMarkers.markers} marker={marker} viewMemory={view.memory} active={!fullScreen} onPainted={pdfPainted} />
          ) : preview.data?.status !== "ready" ? (
            <div className="empty"><Spinner /><p>Готовим изображение источника. Текст доступен сразу.</p><ErrorText error={preview.error} /></div>
          ) : source.data?.metadata_json.kind === "cad" ? (
            <PdfSource url={`/api/v1/sources/${id}/preview?vector=true&full_sheet=${expanded}`} page={0} zoom={zoom} fitPage={expanded} label={`Чертёж, ${sheetLabel}`} />
          ) : (
            <img
              key={id}
              src={`/api/v1/sources/${id}/preview`}
              alt={`Исходный чертёж, лист ${source.data?.sheet || ""}`}
              style={{ width: `${zoom * 100}%`, maxWidth: "none" }}
              onError={() => setFailed(true)}
            />
          )}
        </div>
      )}
      {source.data && (
        <a
          className="source-download"
          href={`/api/v1/documents/${source.data.document_id}/download`}
        >
          <Download size={14} />
          Скачать точный исходник
        </a>
      )}
    </div>
    {fullScreen && <Modal title={source.data?.document_title || [sheetLabel, sheetTitle].filter(Boolean).join(" · ")} fullScreen close={() => setFullScreen(false)}><SourceView id={id} facts={facts} recordKey={recordKey} recordKeys={recordKeys} marker={marker} expanded pageSelection={cadSelection} revisionId={revisionId} onOpenSource={onOpenSource} sharedView={view} /></Modal>}
    </>
  );
}

function WorkInspector({
  work,
  project,
  revisionId,
  run,
  canEdit,
  close,
}: {
  work: Work;
  project: Project;
  revisionId: string;
  run: Run;
  canEdit: boolean;
  close: () => void;
}) {
  const [tab, setTab] = useState<
      "source" | "calculation" | "norms" | "history" | "details"
    >("source"),
    [sourceId, setSourceId] = useState(work.source_ids[0] || "");
  const [sourceKey,setSourceKey]=useState("");
  const viewingIds = [...new Set([...work.source_ids, ...(sourceId ? [sourceId] : [])])];
  const sourceLabels = useSourceLabels(revisionId, viewingIds);
  const previousSourceIds = useRef(work.source_ids);
  useEffect(() => {
    // A plan opened from a room/measurement is a viewing choice, not a new saved
    // work source. Refreshes must keep it; only removal of a saved source resets it.
    if (previousSourceIds.current.includes(sourceId) && !work.source_ids.includes(sourceId)) {
      setSourceId(work.source_ids[0] || "");
      setSourceKey("");
    }
    previousSourceIds.current = work.source_ids;
  }, [work.source_ids]);
  const calculation = useQuery({queryKey: ["calculation", work.id, work.version], enabled: tab === "calculation",
    queryFn: () => result(client.GET("/api/v1/works/{work_id}/calculation", {params: {path: {work_id: work.id}}}))});
  const [edit, setEdit] = useState(false),
    [split, setSplit] = useState(false),
    [similar, setSimilar] = useState(false),
    [statusReason, setStatusReason] = useState(false);
  const facts = useQuery({
    queryKey: ["facts", work.id],
    queryFn: () =>
      result(
        client.GET("/api/v1/works/{work_id}/facts", {
          params: { path: { work_id: work.id } },
        }),
      ),
  });
  const history = useQuery({
    queryKey: ["history", work.id, work.version],
    enabled: tab === "history",
    queryFn: () =>
      result(
        client.GET("/api/v1/works/{work_id}/history", {
          params: { path: { work_id: work.id } },
        }),
      ),
  });
  return (
    <>
      <header className="inspector-heading">
        <div className="eyebrow">
          {work.category} · {work.group || work.discipline}
        </div>
        <button className="icon" onClick={close} aria-label="Закрыть работу">
          <X size={19} />
        </button>
        <h2>{work.description}</h2>
        <div className="inspector-quantity">
          <strong>
            {amount(work.quantity)} <small>{work.unit}</small>
          </strong>
          <span className={`status ${work.status}`}>
            {statusLabel[work.status]}
          </span>
        </div>
        <WorkProducts work={work} openSource={(id,key)=>{setSourceId(id);setSourceKey(key||"");setTab("source");}} />
        {canEdit && (
          <div className="inspector-actions">
            <button onClick={() => setEdit(true)}>{work.status === "approved" ? "Внести корректировку" : "Исправить"}</button>
            <button
              className="primary"
              disabled={work.status === "approved"}
              onClick={() =>
                run(() =>
                  result(
                    client.PATCH("/api/v1/works/{work_id}", {
                      params: { path: { work_id: work.id } },
                      body: {
                        version: work.version,
                        status: "approved",
                        reason: "Проверено специалистом по исходному документу",
                      },
                    }),
                  ),
                )
              }
            >
              <Check size={15} />
              Подтвердить
            </button>
            <button className="quiet" onClick={() => setStatusReason(true)}>
              {work.status === "rejected" ? "Вернуть" : "Исключить"}
            </button>
          </div>
        )}
        {statusReason && (
          <Modal title={work.status === "rejected" ? "Вернуть работу в ведомость" : "Исключить работу"} close={() => setStatusReason(false)}>
            <form onSubmit={(e) => {
              e.preventDefault();
              const reason = String(new FormData(e.currentTarget).get("reason") || "").trim();
              if (!reason) return;
              void run(async () => {
                await result(client.PATCH("/api/v1/works/{work_id}", {
                  params: { path: { work_id: work.id } },
                  body: { version: work.version, status: work.status === "rejected" ? "needs_review" : "rejected", reason },
                }));
                setStatusReason(false);
              });
            }}>
              <Field label={work.status === "rejected" ? "Почему работа возвращается" : "Почему работа исключается"}>
                <textarea name="reason" required maxLength={2000} autoFocus placeholder="Основание решения: документ, лист, письмо заказчика" />
              </Field>
              <p className="muted">Причина сохранится в истории работы вместе с автором и временем.</p>
              <footer><button type="button" onClick={() => setStatusReason(false)}>Отмена</button><button className="primary">{work.status === "rejected" ? "Вернуть" : "Исключить"}</button></footer>
            </form>
          </Modal>
        )}
      </header>
      <WorkAttentionPanel key={work.id} work={work} onNavigate={setTab} />
      <nav className="inspector-tabs">
        {[
          ["source", "Источник"],
          ["calculation", "Расчёт"],
          ["details", "Сведения"],
          ["norms", `Нормы ${work.norms.length}`],
          ["history", "История"],
        ].map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id as typeof tab)}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="inspector-content" key={tab}>
      {tab === "source" && (
        <>
          {viewingIds.length > 1 && (
            <select
              className="source-picker"
              aria-label="Основание работы"
              value={sourceId}
              onChange={(e) => {setSourceId(e.target.value);setSourceKey("");}}
            >
              {viewingIds.map((id, index) => {
                const label=sourceLabels.labels.find(s=>s.id===id);
                return (
                <option key={id} value={id}>
                  {label ? [label.sheet==='Model'?'':sourcePageLabel(label.document,label.sheet),label.document].filter(Boolean).join(' · ') : `Источник ${index+1}`}
                </option>
              );})}
            </select>
          )}
          {sourceId ? (
            <SourceView id={sourceId} recordKey={sourceKey} facts={facts.data || []} revisionId={revisionId} onOpenSource={(id, key) => { setSourceId(id); setSourceKey(key || ""); }} />
          ) : (
            <div className="empty">
              <FileText size={30} />
              <p>
                Источник не указан. Добавьте основание при исправлении работы.
              </p>
            </div>
          )}
        </>
      )}
      {tab === "details" && <WorkDetails key={work.id} projectId={project.id} catalogId={project.catalog_id || null} work={work} canEdit={canEdit} saved={async()=>{await cache.invalidateQueries();}} openSource={(id,key)=>{setSourceId(id);setSourceKey(key||"");setTab("source");}} />}
      {tab === "calculation" && (
        <div className="inspector-body">
          <CalculationGuide work={work}/>
          <h3>Исходные измерения</h3>
          {"quantity" in work.manual && (
            <p className="notice">
              Объём уточнён вручную. Исходные измерения сохранены ниже.
            </p>
          )}
          {facts.data?.map((f) => (
            <button
              key={f.id}
              className="fact"
              onClick={() => {
                setSourceId(f.fragment_id);
                setSourceKey(String(f.context.record_key || ""));
                setTab("source");
              }}
            >
              <span>
                {f.label}
                <small>{f.quote}</small>
              </span>
              <strong>
                {amount(f.value)} {f.unit}
              </strong>
            </button>
          ))}
          {!facts.data?.length && (
            <p className="muted">
              Измерения не выделены.{" "}
              {work.quantity == null
                ? "Число остаётся неизвестным."
                : "Объём внесён специалистом."}
            </p>
          )}
          {calculation.data && <div className="formula"><span>{calculation.data.text}</span></div>}
          <ErrorText error={calculation.error} />
          {work.suggestion.quantity != null && "quantity" in work.manual && (
            <p className="muted">
              Последнее автоматическое предложение:{" "}
              {String(work.suggestion.quantity)} {String(work.suggestion.unit || work.unit)}
            </p>
          )}
          {canEdit && (
            <div className="inline-actions">
              <button onClick={() => setSplit(true)}>Разделить работу</button>
              <button onClick={() => setSimilar(true)}>
                Исправить аналогичные
              </button>
            </div>
          )}
        </div>
      )}
      {tab === "norms" && (
        <NormPicker key={`${work.id}:${project.catalog_id}`} work={work} project={project} run={run} canEdit={canEdit} />
      )}
      {tab === "history" && (
        <div className="inspector-body">
          <ErrorText error={history.error} />
          {history.data?.map((event) => (
            <article className="event" key={event.id}>
              <EventMeta event={event} />
              <p>{reasonText(event.reason)}</p>
              <HistoryChanges before={event.before} after={event.after} action={event.action} />
            </article>
          ))}
          {!history.data?.length && (
            <p className="muted">Решений специалиста ещё нет.</p>
          )}
        </div>
      )}
      </div>
      {edit && <EditWork work={work} run={run} close={() => setEdit(false)} />}
      {split && (
        <SplitWork
          work={work}
          run={run}
          close={() => setSplit(false)}
          done={close}
        />
      )}
      {similar && (
        <SimilarWork work={work} run={run} close={() => setSimilar(false)} />
      )}
    </>
  );
}

function EditWork({
  work,
  run,
  close,
}: {
  work: Work;
  run: Run;
  close: () => void;
}) {
  const base = useRef(work);
  const formRef=useRef<HTMLFormElement>(null);
  const [,acknowledge]=useState(0);
  const [sourceIds, setSourceIds] = useState(work.source_ids);
  return (
    <Modal title={work.status === "approved" ? "Внести корректировку" : "Исправить работу"} close={close}>
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          void run(async () => {
            await result(
              client.PATCH("/api/v1/works/{work_id}", {
                params: { path: { work_id: work.id } },
                body: {
                  version: base.current.version,
                  source_ids: sourceIds,
                  description: String(data.get("description")),
                  quantity:
                    String(data.get("quantity")).trim().replace(",", ".") ||
                    null,
                  unit: String(data.get("unit")),
                  group: String(data.get("group")),
                  mount: String(data.get("mount")),
                  ...buildingChange(base.current.building, String(data.get("building") ?? "")),
                  reason: String(data.get("reason")),
                  ...(base.current.status === "approved" ? {status: "approved" as const} : {}),
                },
              }),
            );
            close();
          });
        }}
      >
        {base.current.version!==work.version&&<div className="notice" role="alert"><p>Пока форма была открыта, работа изменилась. Ваш ввод сохранён.</p><p>Текущая версия: {work.description} · {amount(work.quantity)} {work.unit} · {work.group} · {work.mount}</p><button type="button" onClick={()=>{
          for(const key of ['description','quantity','unit','group','mount','building'] as const){
            const input=formRef.current?.elements.namedItem(key) as HTMLInputElement|null;
            if(input && input.value===String(base.current[key]??''))input.value=String(work[key]??'');
          }
          if(JSON.stringify(sourceIds)===JSON.stringify(base.current.source_ids))setSourceIds(work.source_ids);
          base.current=work;acknowledge(n=>n+1);
        }}>Сверить с текущей версией, сохранив мои изменения</button></div>}
        <Field label="Наименование работы">
          <textarea
            name="description"
            defaultValue={work.description}
            rows={3}
            required
            autoFocus
          />
        </Field>
        {work.status === "approved" && <p className="muted">Было: {work.description} · {amount(work.quantity)} {work.unit}. Новая подтверждённая версия сохранится вместе с причиной, прежняя останется в истории.</p>}
        <div className="form-pair">
          <Field label="Натуральный объём">
            <input
              name="quantity"
              inputMode="decimal"
              defaultValue={work.quantity ?? ""}
              placeholder="Неизвестно"
            />
          </Field>
          <Field label="Единица">
            <input name="unit" defaultValue={work.unit} required />
          </Field>
        </div>
        <Field label="Здание / сооружение">
          <input name="building" defaultValue={work.building} maxLength={200} placeholder="Не указано" />
        </Field>
        <Field label="Тип отделки / марка">
          <input name="group" defaultValue={work.group} />
        </Field>
        <Field label="Способ монтажа">
          <input name="mount" defaultValue={work.mount} />
        </Field>
        <SourcePicker revisionId={work.revision_id} value={sourceIds} change={setSourceIds} />
        <Field label="Основание исправления">
          <textarea
            name="reason"
            required
            placeholder="Что уточнили и по какому источнику"
          />
        </Field>
        <p className="muted">
          {work.status === "approved" ? "Корректировка подтверждается одним действием и сохранится при повторном анализе." : "Правка сохранится при повторном анализе. После изменения строку можно подтвердить."}
        </p>
        <footer>
          <button type="button" onClick={close}>
            Отмена
          </button>
          <button className="primary" disabled={base.current.version!==work.version}>{work.status === "approved" ? "Подтвердить корректировку" : "Сохранить правку"}</button>
        </footer>
      </form>
    </Modal>
  );
}

function NormPicker({
  work,
  project,
  run,
  canEdit,
}: {
  work: Work;
  project: Project;
  run: Run;
  canEdit: boolean;
}) {
  const [query, setQuery] = useState(""), [search, setSearch] = useState("");
  const norms = useQuery({
    queryKey: ["norm-search", project.catalog_id, search],
    enabled: !!project.catalog_id && !!search,
    queryFn: () =>
      result(
        client.GET("/api/v1/catalogs/{catalog_id}/norms", {
          params: {
            path: { catalog_id: project.catalog_id! },
            query: { q: search },
          },
        }),
      ),
  });
  const recommendations = useQuery({queryKey: ["norm-recommendations", work.id, work.version, project.catalog_id], enabled: !!project.catalog_id,
    queryFn: () => result(client.GET("/api/v1/works/{work_id}/norm-recommendations", {params: {path: {work_id: work.id}}}))});
  const [choice, setChoice] = useState<{candidate: Schema["NormRecommendation"]; works: Work[]; applications: Schema["ApplicationOut"][]} | null>(null);
  const [replacementId, setReplacementId] = useState("");
  const choose = (candidate: Schema["NormRecommendation"]) => {
    setChoice({candidate, works: cache.getQueryData<Work[]>(["works", work.revision_id]) || [work], applications: work.norms});
    setReplacementId(work.norms.length === 1 ? work.norms[0].id : work.norms.length ? "" : "add");
  };
  const previous = choice?.applications.find(row => row.id === replacementId);
  const affectedIds = new Set([...(choice?.candidate.work_ids || []), ...(previous?.work_ids || [])]);
  const affected = choice?.works.filter(row => affectedIds.has(row.id)) || [];
  const confirmCorrection = affected.some(row => row.status === "approved" && choice?.candidate.work_ids.includes(row.id));
  const duplicated = !!choice && affected.some(row => choice.candidate.work_ids.includes(row.id)
    && row.norms.some(application => application.norm.id === choice.candidate.norm.id && application.id !== replacementId));
  const [removing, setRemoving] = useState<{application: Schema["ApplicationOut"]; works: Work[]} | null>(null);
  return (
    <div className="inspector-body">
      <p className="muted">
        Объём работы не зависит от выбора нормы. Единицы нормы пересчитываются
        отдельно.
      </p>
      {recommendations.isFetching && <p className="muted"><Spinner /> Подбираем варианты из выбранной базы…</p>}
      {recommendations.error && <div><ErrorText error={recommendations.error}/><button className="quiet" onClick={() => void recommendations.refetch()}>Повторить подбор вариантов</button></div>}
      {!!recommendations.data?.length && <NormAlternatives work={work} candidates={recommendations.data} canEdit={canEdit} choose={choose}/>}
      {recommendations.data?.length === 0 && <p className="muted">Подходящих вариантов по сохранённым данным не найдено. Уточните операцию в поиске ниже.</p>}
      {choice && <Modal title="Выбор расценки" close={() => setChoice(null)}><form onSubmit={e => {
        e.preventDefault(); const reason = String(new FormData(e.currentTarget).get("reason"));
        void run(async () => {
          await result(client.POST("/api/v1/revisions/{revision_id}/norms", {params: {path: {revision_id: work.revision_id}}, body: {
            norm_id: choice.candidate.norm.id, quantity_work_id: choice.candidate.quantity_work_id,
            work_ids: choice.candidate.work_ids, versions: Object.fromEntries(affected.map(row => [row.id, row.version])),
            reason, replace_application_id: previous?.id, confirm_correction: confirmCorrection,
            multiplier: "1",
          }})); setChoice(null);
        });
      }}>
        <h3>{normCode(choice.candidate.norm.code)} · {choice.candidate.norm.title}</h3>
        <p>{choice.candidate.norm.operations.join("; ") || "Состав работ отсутствует"}</p>
        <p>{choice.candidate.norm.technical || "Техническая часть отсутствует. Подбор предварительный."}</p>
        <p>Единица нормы: {choice.candidate.norm.unit || "не указана"}</p>
        <NormResources key={choice.candidate.norm.id} normId={choice.candidate.norm.id}/>
        {!!choice.applications.length && <Field label="Как применить выбранную расценку"><select value={replacementId} onChange={e => setReplacementId(e.target.value)} required>
          <option value="" disabled>Выберите заменяемую расценку или отдельную операцию</option>
          {choice.applications.map(row => <option value={row.id} key={row.id}>Заменить {normCode(row.norm.code)} · {row.norm.title}</option>)}
          <option value="add">Добавить для отдельной операции, сохранив назначенные нормы</option>
        </select></Field>}
        {previous && <p className="notice">Было: {normCode(previous.norm.code)} → станет: {normCode(choice.candidate.norm.code)}. Прежнее применение будет заменено; объёмы работ сохранятся.</p>}
        {replacementId === "add" && !!choice.applications.length && <p className="warning">Дополнительная норма увеличит стоимость. Убедитесь, что её операция ещё не оплачена назначенными нормами.</p>}
        <ul className="preview-list">{affected.map(row => <li key={row.id}>{row.description} · {row.mount} · {amount(row.quantity)} {row.unit}
          {row.id === choice.candidate.quantity_work_id ? " — основание объёма нормы" : ""}
          {!choice.candidate.work_ids.includes(row.id) && <strong className="warning"> — прежняя норма будет снята; новая эту работу не покрывает</strong>}
        </li>)}</ul>
        {affected.length !== affectedIds.size && <p className="warning">Не все затронутые работы загружены. Обновите ведомость перед выбором.</p>}
        {duplicated && <p className="notice">Эта норма уже покрывает выбранные работы. Второе применение не требуется.</p>}
        <Field label="Основание выбора"><textarea name="reason" defaultValue={choice.candidate.origin === "catalog" ? "" : choice.candidate.reason} placeholder="Почему подходит состав работ и какие условия проверены" required /></Field>
        <p className="muted">{confirmCorrection ? "Корректировка подтверждённых работ подтверждается одним действием. Выбор, основание и прежняя расценка сохранятся в истории." : "Выбор, основание и прежняя расценка сохранятся в истории. Работа будет доступна для проверки."}</p>
        <footer><button type="button" onClick={() => setChoice(null)}>Отмена</button><button className="primary" disabled={!replacementId || affected.length !== affectedIds.size || duplicated}>{confirmCorrection ? "Подтвердить корректировку" : "Сохранить выбор"}</button></footer>
      </form></Modal>}
      {work.norms.map((application) => (
        <article className="norm-detail" key={application.id}>
          <div>
            <strong>{normCode(application.norm.code)}</strong>
            <b>
              {amount(application.quantity)} {application.norm.unit}
            </b>
          </div>
          <p>{application.norm.title}</p>
          {application.multiplier !== "1" && (
            <p className="notice">
              Применение нормы: ×{application.multiplier}. {application.multiplier_reason || "Основание не указано."}
            </p>
          )}
          <NormResources key={application.norm.id} normId={application.norm.id}/>
          <details>
            <summary>Состав и техчасть</summary>
            {application.norm.operations.length ? (
              <ul>
                {application.norm.operations.map((op, i) => (
                  <li key={i}>{op}</li>
                ))}
              </ul>
            ) : (
              <p className="warning">
                Состав работ в каталоге отсутствует. Проверьте его в нормативной
                базе.
              </p>
            )}
            <p>
              {application.norm.technical || "Техническая часть не загружена."}
            </p>
            <p>{application.reason}</p>
            {application.work_ids.length > 1 && (
              <p>
                Комплексная норма покрывает {application.work_ids.length}{" "}
                работы. Объём учтён один раз.
              </p>
            )}
          </details>
          {application.error && <p className="error">{application.error}</p>}
          {canEdit && <button className="quiet" onClick={() => {
            const all = cache.getQueryData<Work[]>(["works", work.revision_id]) || [work];
            setRemoving({application, works: all.filter(row => application.work_ids.includes(row.id))});
          }}>Убрать норму</button>}
        </article>
      ))}
      {canEdit && (
        <>
          <form
            className="norm-search"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(query);
            }}
          >
            <Field label="Найти дополнительную норму">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Шифр или содержание работы"
                disabled={!project.catalog_id}
              />
            </Field>
            <button disabled={!query || !project.catalog_id}>
              <Search size={16} />
              Найти
            </button>
          </form>
          {!project.catalog_id && (
            <p className="warning">В проекте не выбрана нормативная база.</p>
          )}
          <ErrorText error={norms.error} />
          {norms.isFetching && <Spinner />}
          {norms.data?.map((n) => (
            <button
              className="norm-option"
              key={n.id}
              onClick={() => choose({norm: n, quantity_work_id: work.id, work_ids: [work.id], reason: "", origin: "catalog", warnings: [], can_apply: true, catalog_label: ""})}
            >
              <strong>
                {normCode(n.code)}
                <span>{n.unit}</span>
              </strong>
              <span>{n.title}</span>
            </button>
          ))}
          {norms.data?.length === 0 && (
            <p className="muted">
              Совпадений нет. Попробуйте название операции или часть шифра.
            </p>
          )}
        </>
      )}
      {removing && <Modal title="Убрать применение нормы" close={() => setRemoving(null)}><form onSubmit={e => {
        e.preventDefault(); const reason = String(new FormData(e.currentTarget).get("reason"));
        void run(async () => { await result(client.DELETE("/api/v1/norm-applications/{application_id}", { params: {path: {application_id: removing.application.id}}, body: {versions: Object.fromEntries(removing.works.map(row => [row.id, row.version])), reason} })); setRemoving(null); });
      }}><p>{normCode(removing.application.norm.code)} будет убрана у следующих работ:</p><ul className="preview-list">{removing.works.map(row => <li key={row.id}>{row.description}</li>)}</ul><Field label="Основание исключения"><textarea name="reason" required /></Field><footer><button className="primary" disabled={removing.works.length !== removing.application.work_ids.length}>Убрать норму у {removing.works.length} работ</button></footer></form></Modal>}
    </div>
  );
}

function SplitWork({
  work,
  run,
  close,
  done,
}: {
  work: Work;
  run: Run;
  close: () => void;
  done: () => void;
}) {
  const [parts, setParts] = useState([
    { description: work.description, quantity: "", building: work.building },
    { description: work.description, quantity: "", building: work.building },
  ]);
  return (
    <Modal title="Разделить работу" close={close}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          void run(async () => {
            await result(
              client.POST("/api/v1/works/{work_id}/split", {
                params: { path: { work_id: work.id } },
                body: {
                  version: work.version,
                  reason: String(data.get("reason")),
                  parts: parts.map((p) => ({
                    ...p,
                    building: p.building.trim(),
                    quantity: p.quantity.replace(",", "."),
                    source_ids: work.source_ids,
                  })),
                },
              }),
            );
            close();
            done();
          });
        }}
      >
        <p>
          Исходный объём:{" "}
          <b>
            {amount(work.quantity)} {work.unit}
          </b>
          . Сумма частей должна совпасть с ним.
        </p>
        {parts.map((part, index) => (
          <div className="split-part" key={index}>
            <input
              aria-label={`Название части ${index + 1}`}
              required
              value={part.description}
              onChange={(e) =>
                setParts((v) =>
                  v.map((p, i) =>
                    i === index ? { ...p, description: e.target.value } : p,
                  ),
                )
              }
            />
            <input
              aria-label={`Объём части ${index + 1}`}
              required
              inputMode="decimal"
              value={part.quantity}
              onChange={(e) =>
                setParts((v) =>
                  v.map((p, i) =>
                    i === index ? { ...p, quantity: e.target.value } : p,
                  ),
                )
              }
            />
            <input
              className="split-building"
              aria-label={`Здание / сооружение части ${index + 1}`}
              placeholder="Здание / сооружение не указано"
              maxLength={200}
              value={part.building}
              onChange={(e) =>
                setParts((v) =>
                  v.map((p, i) =>
                    i === index ? { ...p, building: e.target.value } : p,
                  ),
                )
              }
            />
          </div>
        ))}
        <button
          type="button"
          className="quiet"
          onClick={() =>
            setParts((v) => [...v, { description: "", quantity: "", building: work.building }])
          }
        >
          <Plus size={14} />
          Ещё часть
        </button>
        <Field label="Основание разделения">
          <textarea name="reason" required />
        </Field>
        <footer>
          <button className="primary">Разделить</button>
        </footer>
      </form>
    </Modal>
  );
}

function SimilarWork({
  work,
  run,
  close,
}: {
  work: Work;
  run: Run;
  close: () => void;
}) {
  const similar = useQuery({
    queryKey: ["similar", work.id],
    queryFn: () =>
      result(
        client.POST("/api/v1/works/{work_id}/similar", {
          params: { path: { work_id: work.id } },
        }),
      ),
  });
  const [excluded, setExcluded] = useState<string[]>([]);
  return (
    <Modal title="Исправить аналогичные работы" close={close}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          void run(async () => {
            await result(
              client.POST("/api/v1/works/batch", {
                body: {
                  versions: Object.fromEntries(
                    (similar.data || [])
                      .filter((w) => !excluded.includes(w.id))
                      .map((w) => [w.id, w.version]),
                  ),
                  description: String(data.get("description")),
                  reason: String(data.get("reason")),
                },
              }),
            );
            close();
          });
        }}
      >
        <p>Проверьте конкретный список. Объёмы каждой позиции сохранятся.</p>
        <div className="preview-list">
          {similar.data?.map((w) => (
            <label key={w.id}>
              <input
                type="checkbox"
                checked={!excluded.includes(w.id)}
                onChange={(e) =>
                  setExcluded((v) =>
                    e.target.checked
                      ? v.filter((id) => id !== w.id)
                      : [...v, w.id],
                  )
                }
              />
              {w.description}
              <b>
                {amount(w.quantity)} {w.unit}
              </b>
            </label>
          ))}
        </div>
        <Field label="Новое описание для выбранных работ">
          <textarea
            name="description"
            defaultValue={work.description}
            required
          />
        </Field>
        <Field label="Причина">
          <textarea name="reason" required />
        </Field>
        <footer>
          <button className="primary" disabled={!similar.data?.length}>
            Применить к выбранным
          </button>
        </footer>
      </form>
    </Modal>
  );
}

function AddWork({
  sections,
  rows,
  revision,
  documents,
  run,
  close,
  created,
}: {
  sections: string[];
  rows: Work[];
  revision: Revision;
  documents: Schema["DocumentOut"][];
  run: Run;
  close: () => void;
  created: (id: string) => void;
}) {
  const sectionChoices = sectionOptions(sections, rows);
  const [discipline, setDiscipline] = useState(sectionChoices[0] ?? "");
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  return (
    <Modal title="Добавить пропущенную работу" close={close}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          void run(async () => {
            const work = await result(
              client.POST("/api/v1/revisions/{revision_id}/works", {
                params: { path: { revision_id: revision.id } },
                body: {
                  discipline,
                  category: String(data.get("category")),
                  description: String(data.get("description")),
                  group: String(data.get("group")),
                  building: String(data.get("building")),
                  mount: String(data.get("mount")),
                  unit: String(data.get("unit")),
                  quantity:
                    String(data.get("quantity")).replace(",", ".") || null,
                  source_ids: sourceIds,
                  reason: String(data.get("reason")),
                },
              }),
            );
            created(work.id);
            close();
          });
        }}
      >
        <div className="form-pair">
          <Field label="Раздел">
            <select
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value)}
              required
            >
              {sectionChoices.map(value => <option key={value}>{value}</option>)}
            </select>
          </Field>
          <Field label="Группа работ">
            <select name="category">
              {categoryOptions(discipline, rows, categories).map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Работа">
          <textarea name="description" required autoFocus />
        </Field>
        <div className="form-pair">
          <Field label="Здание / корпус">
            <input name="building" />
          </Field>
          <Field label="Тип / марка">
            <input name="group" />
          </Field>
        </div>
        <Field label="Способ монтажа">
          <input name="mount" />
        </Field>
        <div className="form-pair">
          <Field label="Объём">
            <input name="quantity" inputMode="decimal" />
          </Field>
          <Field label="Единица">
            <input name="unit" required defaultValue="м²" />
          </Field>
        </div>
        <SourcePicker revisionId={revision.id} value={sourceIds} change={setSourceIds} />
        <Field label="Основание добавления">
          <textarea name="reason" required />
        </Field>
        <footer>
          <button className="primary">Добавить работу</button>
        </footer>
      </form>
    </Modal>
  );
}

function DocumentBuilding({doc, run, busy}: {doc: Schema["DocumentOut"]; run: Run; busy: boolean}) {
  const previous = String(doc.metadata_json.building || "");
  const [building, setBuilding] = useState(previous), [reason, setReason] = useState("");
  const changed = building.trim() !== previous;
  return <form className="document-building" onSubmit={event => {
    event.preventDefault();
    void run(() => result(client.PUT("/api/v1/documents/{document_id}/building", {
      params: {path: {document_id: doc.id}}, body: {building: building.trim(), previous_building: previous, reason: reason.trim()},
    })));
  }}>
    <Field label="Здание / сооружение">
      <input value={building} onChange={e => setBuilding(e.target.value)} list="document-buildings" maxLength={200}
        disabled={busy} placeholder="Например, административное здание или КПП" />
    </Field>
    {changed && <>
      <Field label="Основание привязки"><input value={reason} onChange={e => setReason(e.target.value)} required maxLength={4000} disabled={busy} /></Field>
      <button className="primary" disabled={busy || !reason.trim()}>Сохранить привязку</button>
      <small>Объёмы и ручные решения сохранятся. Связанные работы будут отмечены для проверки.</small>
    </>}
    {[".zip", ".rar", ".7z"].includes(doc.extension) && <small>Привязка применяется и к файлам архива; отдельные назначения других зданий сохраняются.</small>}
    {busy && <small>Привязку можно изменить после завершения обработки.</small>}
  </form>;
}

function Materials({
  revision,
  documents,
  jobs,
  run,
  canEdit,
  selectSource,
}: {
  revision: Revision;
  documents: Schema["DocumentOut"][];
  jobs: Schema["JobOut"][];
  run: Run;
  canEdit: boolean;
  selectSource: (id: string) => void;
}) {
  const autonomous = useAutonomousUploads(revision.project_id);
  const input = useRef<HTMLInputElement>(null),
    [expanded, setExpanded] = useState(""),
    [uploadBuilding, setUploadBuilding] = useState(""),
    [buildingFilter, setBuildingFilter] = useState(""),
    [filter, setFilter] = useState("");
  const buildings = [...new Set(documents.map(d => String(d.metadata_json.building || "")).filter(Boolean))].sort((a,b) => a.localeCompare(b, "ru", {numeric:true}));
  const processing = jobs.some(job => ["queued", "running"].includes(job.status));
  const source = useDocumentSources(expanded);
  const labels: Record<string, string> = {
    ready: "Готов к анализу",
    queued: "В очереди",
    cancelled: "Остановлено",
    running: "Чтение",
    failed: "Ошибка",
    container: "Архив разобран",
    duplicate: "Повторный файл",
  };
  const upload = (files: FileList | null) => {
    if (!files) return;
    const selectedFiles = Array.from(files);
    void run(async () => {
      const { data: capability } = await autonomous.refetch({ throwOnError: true });
      if (!capability) throw new Error("Не удалось проверить режим загрузки. Повторите загрузку.");
      if (capability.enabled) {
        const batch = await uploadAutonomous(revision.id, selectedFiles, uploadBuilding.trim());
        if (batch.revision_id !== revision.id) {
          const url = new URL(window.location.href);
          url.searchParams.set("revision", batch.revision_id);
          window.location.assign(url.toString());
        }
        return;
      }
      for (const file of selectedFiles) {
        const form = new FormData();
        form.append("file", file);
        form.append("building", uploadBuilding.trim());
        const response = await fetch(
          `/api/v1/revisions/${revision.id}/documents`,
          { method: "POST", headers: { "x-workbench": "1" }, body: form },
        );
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail);
        }
      }
    });
  };
  return (
    <main className="materials">
      <AutonomousProcessing projectId={revision.project_id} canEdit={canEdit} />
      <div className="material-intro">
        <div>
          <h2>Исходные материалы</h2>
          <p>
            Сохраняются все файлы и вложенные папки. Каждый чертёж
            обрабатывается отдельно.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() =>
              run(() =>
                result(
                  client.PUT("/api/v1/revisions/{revision_id}", {
                    params: { path: { revision_id: revision.id } },
                    body: {
                      name: revision.name,
                      kind: revision.kind,
                      confirmed: !revision.confirmed,
                    },
                  }),
                ),
              )
            }
          >
            {revision.confirmed ? (
              <>
                <Check size={16} />
                Комплект подтверждён
              </>
            ) : (
              "Подтвердить состав комплекта"
            )}
          </button>
        )}
      </div>
      <datalist id="document-buildings">{buildings.map(name => <option key={name} value={name} />)}</datalist>
      {canEdit && (!revision.confirmed || autonomous.data?.enabled) && (<>
        <div className="upload-building"><Field label="Здание для загружаемых файлов (необязательно)">
          <input value={uploadBuilding} onChange={e => setUploadBuilding(e.target.value)} list="document-buildings"
            maxLength={200} placeholder="Например, административное здание или КПП" disabled={processing} />
        </Field></div>
        <div
          className="upload-zone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!processing) upload(e.dataTransfer.files);
          }}
        >
          <Upload size={24} />
          <div>
            <strong>Перетащите файлы или архив проекта</strong>
            <span>DWG, DXF, PDF, XLSX, DOCX, XML, GGE · ZIP, RAR, 7z</span>
          </div>
          <button onClick={() => input.current?.click()} disabled={processing}>Выбрать файлы</button>
          <input
            ref={input}
            type="file"
            disabled={processing}
            multiple
            hidden
            onChange={(e) => {
              upload(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </>)}
      <div className="material-counts">
        <span>
          <b>{documents.length}</b> файлов
        </span>
        <span>
          <b>{documents.filter((d) => d.status === "ready").length}</b>{" "}
          подготовлено
        </span>
        <span>
          <b>{documents.filter((d) => d.status === "failed").length}</b> ошибок
        </span>
        {(buildings.length > 0 || buildingFilter) && <select aria-label="Здание в материалах" value={buildingFilter} onChange={e => setBuildingFilter(e.target.value)}>
          <option value="">Все здания</option>
          {buildings.map(name => <option key={name}>{name}</option>)}
          <option value="__unassigned">Здание не указано</option>
        </select>}
        <div className="search">
          <Search size={15} />
          <input
            aria-label="Поиск файлов"
            placeholder="Найти файл"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>
      <div className="documents">
        {documents
          .filter((d) => d.path.toLowerCase().includes(filter.toLowerCase()) &&
            (!buildingFilter || (d.metadata_json.building || "__unassigned") === buildingFilter))
          .map((doc) => (
            <article className="document" key={doc.id}>
              <div className="document-row">
                <span className="file-badge">
                  {doc.extension.slice(1).toUpperCase()}
                </span>
                <button
                  className="document-name"
                  onClick={() =>
                    setExpanded((v) => (v === doc.id ? "" : doc.id))
                  }
                >
                  <strong>{doc.path.split("/").pop()}</strong>
                  <small>
                    {doc.path} · {(doc.size / 1024 / 1024).toFixed(1)} МБ
                    {doc.metadata_json.building ? ` · ${doc.metadata_json.building}` : ""}
                  </small>
                </button>
                <span
                  className={`status ${doc.status === "ready" ? "approved" : doc.status === "failed" ? "needs_review" : "pending"}`}
                >
                  {labels[doc.status] || doc.status}
                </span>
                <a
                  className="icon"
                  aria-label={`Скачать ${doc.path}`}
                  href={`/api/v1/documents/${doc.id}/download`}
                >
                  <Download size={17} />
                </a>
                {canEdit && ["failed", "cancelled"].includes(doc.status) && (
                  <button
                    className="quiet"
                    onClick={() =>
                      run(() =>
                        result(
                          client.POST("/api/v1/documents/{document_id}/retry", {
                            params: { path: { document_id: doc.id } },
                          }),
                        ),
                      )
                    }
                  >
                    Повторить
                  </button>
                )}
              </div>
              {doc.error && <p className="error document-error">{doc.error}</p>}
              {expanded === doc.id && (
                <div className="document-sources">
                  {canEdit && <DocumentBuilding key={`${doc.id}:${doc.metadata_json.building || ""}`} doc={doc} run={run} busy={processing} />}
                  <p>
                    Загружено фрагментов: {source.data?.pages.flat().length ?? 0}
                    {source.hasNextPage && " · доступны следующие"}
                  </p>
                  {source.isPending && <p role="status">Загружаем источники…</p>}
                  {source.error && <p role="alert">Не удалось загрузить источники. <button onClick={() => void source.refetch()}>Повторить загрузку</button></p>}
                  {source.isSuccess && !source.data.pages.flat().length && <p>Фрагменты ещё не извлечены. Точный исходник доступен для скачивания.</p>}
                  {source.hasNextPage && <button onClick={() => void source.fetchNextPage()} disabled={source.isFetchingNextPage}>Ещё фрагменты</button>}
                  {source.data?.pages.flat().map((s, index) => (
                    <button key={s.id} onClick={() => selectSource(s.id)}>
                      <FileText size={15} />
                      {sourcePageLabel(doc.path,s.sheet)} · фрагмент {index + 1}
                      <ArrowRight size={14} />
                    </button>
                  ))}
                </div>
              )}
            </article>
          ))}
      </div>
      {jobs.find(job => job.kind === "analyze" && job.details?.coverage) && <details className="analysis-coverage"><summary>Что проверено и что осталось уточнить</summary>{Object.entries(jobs.find(job => job.kind === "analyze" && job.details?.coverage)!.details.coverage as Record<string, {status: string; works?: number; notes?: string[]; error?: string}>).map(([category, report]) => <section key={category}><h3>{category} · {report.status === "done" ? `${report.works ?? 0} работ в предложении` : report.status === "partial" ? "Сохранена часть работ" : "Не завершено"}</h3>{report.error && <p className="error">{report.error}</p>}<CoverageNotes notes={report.notes}/></section>)}</details>}
      <details className="job-log">
        <summary>Журнал обработки</summary>
        {jobs.map((job) => (
          <div key={job.id}>
            <time>{new Date(job.updated_at).toLocaleString("ru-RU")}</time>
            <span>{jobKindText(job)}</span>
            <span>{jobStatusText(job)}</span>
            <p>{job.message}</p>
            {job.budget && <p>Бюджет ${Number(job.budget.limit_usd).toFixed(2)} · расчётный расход ${Number(job.budget.spent_usd).toFixed(2)} · резерв ${Number(job.budget.reserved_usd).toFixed(2)} · доступно ${Number(job.budget.remaining_usd).toFixed(2)}{job.budget.unknown_requests > 0 && " · есть запрос с неподтверждённым списанием"}</p>}
          </div>
        ))}
      </details>
      {jobs.filter(job => job.kind === "final_review").map(job => <FinalReviewReport key={job.id} job={job} canEdit={canEdit}
        resume={() => run(() => result(client.POST("/api/v1/jobs/{job_id}/resume-review", { params: { path: { job_id: job.id } } })))}
        cancel={() => run(() => result(client.POST("/api/v1/jobs/{job_id}/cancel", { params: { path: { job_id: job.id } } })))} />)}
    </main>
  );
}

export function Comparison({
  revisions,
  run,
  canEdit,
}: {
  revisions: Revision[];
  run: Run;
  canEdit: boolean;
}) {
  const [before, setBefore] = useState(
      revisions.find((r) => r.kind === "before")?.id || revisions[0]?.id || "",
    ),
    [after, setAfter] = useState(
      revisions.find((r) => r.kind === "after")?.id || revisions[1]?.id || "",
    );
  const [source, setSource] = useState(""),
    [matching, setMatching] = useState(false),
    [unmatching, setUnmatching] = useState("");
  const differences = useQuery({
    queryKey: ["compare", before, after],
    enabled: !!before && !!after && before !== after,
    queryFn: () =>
      result(
        client.GET("/api/v1/compare", { params: { query: { before, after } } }),
      ),
  });
  const [all, setAll] = useState(false);
  const [view, setView] = useState<'actions' | 'classic'>('actions');
  const pairReady = !!before && !!after && before !== after;
  const editingMatch = matching || !!unmatching;
  const renderDifference = (item: Schema['Difference']) => {
    const delta = revisionDelta(item);
    return <div className={`diff-row ${item.kind}`}>
      <span className="status">{revisionKindLabels[item.kind]}{canEdit && item.match_id && <button className="quiet" onClick={() => setUnmatching(item.match_id!)}>Отменить сопоставление</button>}</span>
      {[item.before, item.after].map((side, i) => <div key={i}>{side ? <>
        <strong>{side.description}</strong>
        {side.mount && <p>{side.mount}</p>}
        <p>{side.quantity == null || side.quantity.trim() === '' ? 'Объём не установлен' : `${amount(side.quantity)} ${side.unit}`}</p>
        {side.source_ids[0] && <button className="quiet" onClick={() => setSource(side.source_ids[0])}><FileText size={14} />Основание</button>}
      </> : <span className="muted">Нет позиции</span>}</div>)}
      <div className="quantity">{delta.value == null ? '—' : amount(delta.value)}<small>{delta.unit || delta.note}</small>{item.reason && <p className="muted">{item.reason}</p>}</div>
    </div>;
  };
  return (
    <main className="comparison">
      <div className="compare-heading">
        <h2>Сравнение ведомостей</h2>
        <p>
          У каждой стороны сохраняется собственное основание. «Удалено» означает,
          что позиции нет в ведомости «Стало»: перед выводом об изменении проекта
          проверьте полноту обеих ведомостей по исходникам.
        </p>
      </div>
      <div className="compare-selectors">
        <Field label="Было">
          <select disabled={editingMatch} value={before} onChange={(e) => setBefore(e.target.value)}>
            <option value="">Выберите комплект</option>
            {revisions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {!r.confirmed ? " · не подтверждён" : ""}
              </option>
            ))}
          </select>
        </Field>
        <ArrowRight size={20} />
        <Field label="Стало">
          <select disabled={editingMatch} value={after} onChange={(e) => setAfter(e.target.value)}>
            <option value="">Выберите комплект</option>
            {revisions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {!r.confirmed ? " · не подтверждён" : ""}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <ErrorText error={differences.error} />
      {!pairReady && (
        <div className="empty">
          <Layers3 size={32} />
          <h2>Нужны две редакции проекта</h2>
          <p>
            Добавьте комплекты «было» и «стало» и подтвердите их состав в
            материалах.
          </p>
        </div>
      )}
      {pairReady && differences.isPending && <p role="status">Сравниваем ведомости…</p>}
      {pairReady && differences.data && (
        <>
          <div className="ra-toggle" role="group" aria-label="Вид сравнения редакций">
            <button type="button" disabled={editingMatch} aria-pressed={view === 'actions'} onClick={() => setView('actions')}>По действиям · Бета</button>
            <button type="button" disabled={editingMatch} aria-pressed={view === 'classic'} onClick={() => setView('classic')}>Классический</button>
          </div>
          <div className="compare-tools">
            <label>
              <input
                type="checkbox"
                disabled={editingMatch}
                checked={all}
                onChange={(e) => setAll(e.target.checked)}
              />
              Показать без изменений
            </label>
            {canEdit && (
              <button onClick={() => setMatching(true)}>
                Сопоставить работы вручную
              </button>
            )}
          </div>
          {view === 'actions' ? <RevisionActionView key={`${before}:${after}`} items={differences.data.filter(d => all || d.kind !== 'unchanged')} renderDetail={renderDifference}/> : <div className="diff-table">
            <div className="diff-head">
              <span>Изменение</span>
              <span>Было</span>
              <span>Стало</span>
              <span>Δ</span>
            </div>
            {differences.data
              .filter((d) => all || d.kind !== "unchanged")
              .map((item, index) => (
                <React.Fragment key={`${item.before?.id || ''}:${item.after?.id || ''}:${index}`}>{renderDifference(item)}</React.Fragment>
              ))}
          </div>}
        </>
      )}
      {source && (
        <Modal wide title="Основание изменения" close={() => setSource("")}>
          <SourceView id={source} />
        </Modal>
      )}
      {unmatching && (
        <Modal title="Отменить сопоставление" close={() => setUnmatching("")}>
          <form onSubmit={(e) => {
            e.preventDefault();
            const reason = String(new FormData(e.currentTarget).get("reason") || "").trim();
            if (!reason) return;
            void run(async () => {
              await result(client.DELETE("/api/v1/matches/{match_id}", {params: {path: {match_id: unmatching}}, body: {reason}}));
              setUnmatching("");
            });
          }}>
            <Field label="Почему сопоставление отменяется"><textarea name="reason" required maxLength={2000} autoFocus placeholder="Например: это разные работы, совпало только название" /></Field>
            <footer><button type="button" onClick={() => setUnmatching("")}>Отмена</button><button className="primary">Отменить сопоставление</button></footer>
          </form>
        </Modal>
      )}
      {matching && (
        <Modal title="Сопоставить работы" close={() => setMatching(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              void run(async () => {
                await result(
                  client.POST("/api/v1/matches", {
                    body: {
                      before_id: String(data.get("before")),
                      after_id: String(data.get("after")),
                      replacement: data.get("replacement") === "on",
                      reason: String(data.get("reason")),
                    },
                  }),
                );
                setMatching(false);
              });
            }}
          >
            <Field label="Работа в исходной редакции">
              <select name="before" required>
                {differences.data
                  ?.filter((d) => d.before && !d.after)
                  .map((d) => (
                    <option value={d.before!.id} key={d.before!.id}>
                      {d.before!.description} · {amount(d.before!.quantity)}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Работа в новой редакции">
              <select name="after" required>
                {differences.data
                  ?.filter((d) => d.after && !d.before)
                  .map((d) => (
                    <option value={d.after!.id} key={d.after!.id}>
                      {d.after!.description} · {amount(d.after!.quantity)}
                    </option>
                  ))}
              </select>
            </Field>
            <label>
              <input type="checkbox" name="replacement" />
              Это замена одной работы другой
            </label>
            <Field label="Основание сопоставления"><textarea name="reason" required maxLength={2000} placeholder="Почему это одна работа или её замена" /></Field>
            <footer>
              <button className="primary">Сохранить соответствие</button>
            </footer>
          </form>
        </Modal>
      )}
    </main>
  );
}

function SourceComparison({issue,revisionId,close}:{issue:Schema['ProtocolIssue'];revisionId:string;close:()=>void}) {
  const [side,setSide]=useState(0);
  const [sourceIds,setSourceIds]=useState([issue.left,issue.right].map(m=>m?.addresses?.find(a=>m.source_ids.includes(a.source_id))?.source_id||m?.source_ids[0]||''));
  const [records,setRecords]=useState(['','']);
  const sourceLabels=useSourceLabels(revisionId,[...(issue.left?.source_ids||[]),...(issue.right?.source_ids||[])]);
  const labels:Record<string,string>={vor:'ВОР',specification:'Спецификация',plan:'Графическая часть',cable_journal:'Кабельный журнал',technical:'Техническая часть'};
  return <Modal title={issue.reason} fullScreen close={close}><div className="source-comparison">
    <div className="comparison-mobile-tabs" role="tablist" aria-label="Сравниваемые источники">{[issue.left,issue.right].map((m,n)=><button key={n} role="tab" title={m?.label} aria-selected={side===n} onClick={()=>setSide(n)}>{n+1}. {labels[m?.role||'']||'Источник'}</button>)}</div>
    <div className="source-comparison-panes">{[issue.left,issue.right].map((m,n)=>{if(!m)return null;const keys=[...new Set(m.addresses?.filter(a=>a.source_id===sourceIds[n]).map(a=>a.record_key)||[])];const selected=keys.includes(records[n])?records[n]:keys[0];return <section key={m.id} className={side===n?'selected':''} aria-label={m.label}>
      <header><strong>{m.label}</strong><span className={`comparison-marker-value ${n===0?'amber':'blue'}`}>{m.value==null?'Неизвестно':amount(m.value)} {m.unit}</span>{m.source_ids.length>1&&<select aria-label={`Источник: ${m.label}`} value={sourceIds[n]} onChange={e=>setSourceIds(ids=>ids.map((id,i)=>i===n?e.target.value:id))}>{m.source_ids.map((id,i)=>{const label=sourceLabels.labels.find(l=>l.id===id);return <option key={id} value={id}>{label?`${label.document} · ${sourcePageLabel(label.document,label.sheet)}`:`Источник ${i+1}`}</option>;})}</select>}
      {keys.length>1&&<select aria-label={`Область: ${m.label}`} value={selected} onChange={e=>setRecords(v=>v.map((key,i)=>i===n?e.target.value:key))}>{keys.map((key,i)=><option key={key} value={key}>Область {i+1} из {keys.length}</option>)}</select>}</header>
      {!keys.length&&<p className="cad-page-warning">Точная строка значения не установлена. Открыт полный лист; отметка не показана.</p>}<SourceView key={sourceIds[n]} id={sourceIds[n]} recordKey={selected} recordKeys={keys} marker={{precision:m.addresses?.find(a=>a.source_id===sourceIds[n]&&a.record_key===selected)?.precision||m.address_state,tone:n===0?'amber':'blue',label:`${m.value==null?'Неизвестно':amount(m.value)} ${m.unit}`}} expanded revisionId={revisionId} onOpenSource={(id,key)=>{setSourceIds(values=>values.map((value,index)=>index===n?id:value));setRecords(values=>values.map((value,index)=>index===n?(key||''):value));}} />
    </section>;})}</div>
    <p className="comparison-footer">{issue.formula&&<span>{issue.formula}<br/></span>}{issue.scope} · {issue.difference==null?'Числовое сравнение пока недоступно':`Разница ${amount(issue.difference)} ${issue.unit} · ${issue.percent==null?'процент не определён':`${Number(issue.percent).toLocaleString('ru-RU',{maximumFractionDigits:2})}%`}${issue.base?` · база: ${issue.base}`:''}`}</p>
  </div></Modal>;
}

function RevisionHistory({revisionId,openWork}:{revisionId:string;openWork:(id:string)=>void}) {
  const [cursors,setCursors]=useState<(string|undefined)[]>([undefined]);
  const [filter,setFilter]=useState<HistoryFilter>({group:'',origin:''});
  const before=cursors[cursors.length-1];
  const query=useQuery({queryKey:['revision-history',revisionId,before,filter],queryFn:()=>result(client.GET('/api/v1/revisions/{revision_id}/history',{params:{path:{revision_id:revisionId},query:{before_id:before,limit:30,action:groupActions(filter.group),origin:filter.origin||undefined}}}))});
  return <section className="review-register revision-history"><h2>История корректировок</h2><p>Изменения работ выбранной редакции и настроек проекта. Предыдущие значения сохраняются и после замены работы.</p>
    <HistoryFilters value={filter} change={value=>{setFilter(value);setCursors([undefined]);}}/>
    {query.isPending&&<p>Загружаем историю…</p>}<ErrorText error={query.error}/>
    {query.data?.events.map(event=><article className="event" key={event.id}><EventMeta event={event}/>
      <h3>{event.work_id&&event.work_active?<button className="quiet" onClick={()=>openWork(event.work_id!)}>{event.work_description}</button>:event.work_description}</h3>
      <p>{reasonText(event.reason)}</p><HistoryChanges before={event.before} after={event.after} action={event.action}/>
    </article>)}
    {query.data?.events.length===0&&<p>{filter.group||filter.origin?'По выбранному фильтру записей нет.':'Корректировок ещё нет.'}</p>}
    <nav className="register-pagination"><button disabled={cursors.length===1||query.isPending} onClick={()=>setCursors(v=>v.slice(0,-1))}>Назад</button><span>Страница {cursors.length}</span><button disabled={!query.data?.next_id||query.isPending} onClick={()=>setCursors(v=>[...v,query.data!.next_id!])}>Далее</button></nav>
  </section>;
}

function HistoryChanges({before,after,action=''}:{before:Record<string,unknown>;after:Record<string,unknown>;action?:string}) {
  if('parameters' in before||'parameters' in after)return <EstimateParameterChanges before={before.parameters} after={after.parameters}/>;
  const names:Record<string,string>={description:"Вид работы",quantity:"Объём",unit:"Единица",status:"Проверка",group:"Тип / марка",mount:"Монтаж",category:"Подраздел",discipline:"Раздел",source_ids:"Источники",norms:"Нормы",measurements:"Измерения",contributions:"Помещения",resources:"Материалы и оборудование",sequence:"Порядок выполнения",composition:"Состав из проекта",state:"Решение по замечанию",decision:"Решение по позиции ВОР",view_pages:"Границы просмотра",service_tier:"Режим обработки",limit_usd:"Предел расходов на анализ, USD"};
  names.building = "Здание / сооружение";
  Object.assign(names,{name:'Название',task:'Задание',sections:'Разделы проекта',kind:'Роль редакции в сравнении',confirmed:'Состав редакции',active:'В ведомости',issues:'Замечания'});
  const numeric=new Set(['quantity','value','known_quantity']);
  const show=(key:string,value:unknown):string => {
    if(value==null)return key==='decision'?'Решение ещё не принято':'Не задано';
    if(key==='service_tier')return value==='default'?'Standard':value==='flex'?'Flex':String(value);
    if(key==='status')return statusLabel[String(value)]||String(value);
    if(key==='confirmed')return value?'Подтверждён':'Не подтверждён';
    if(key==='active')return value?'Да':'Нет — работа заменена или снята';
    if(key==='kind')return ({current:'Текущая',before:'До изменений',after:'После изменений'} as Record<string,string>)[String(value)]||String(value);
    if(key==='state')return ({open:'Открыто',designer:'Уточнение проектировщику',resolved:'Рассмотрено',clear:'Противоречий не выявлено',attention:'Есть замечания'} as Record<string,string>)[String(value)]||String(value);
    if(key==='decision'&&typeof value==='object') {
      const decision=value as {action?:string;reason?:string};
      const label=decision.action==='accept_removal'?'Удаление принято':decision.action==='reopen'?'Возвращено на проверку':'Решение записано';
      return [label,decision.reason].filter(Boolean).join(' · ');
    }
    if(numeric.has(key))return amount(value as string);
    if(Array.isArray(value))return value.length?value.map(v=>{
      if(typeof v!=='object'||!v)return key==='source_ids'?`Источник ${String(v).slice(0,8)}`:String(v);
      return [v.name||v.place||v.label||(v.code&&v.title?`${v.code} · ${v.title}`:'')||v.title||v.code||normCode(v.norm?.code||v.norm_code)||(v.norm_id?'Норма '+String(v.norm_id).slice(0,8):'Запись'),v.norm?.title,v.quantity!=null||v.value!=null?amount(v.quantity??v.value):null,v.unit||v.norm?.unit,
        v.is_work_total?'Объём всей работы':'',
        v.price_status?({found:'Цена найдена',price_absent:'Цена отсутствует',catalog_unknown:'Базу уточнить',ambiguous:'Соответствие уточнить',specification_missing:'Характеристики уточнить'} as Record<string,string>)[v.price_status]||v.price_status:'']
        .filter(x=>x!=null&&x!=='').join(' · ');
    }).join('; '):'Нет';
    if(typeof value==='object')return 'text' in value?String(value.text):'Сведения изменены — подробности ниже';
    return String(value);
  };
  const changed=changedKeys(Object.keys(names),before,after,action);
  const successors=successorsText(after,amount);
  const summary=changed.length?'':eventSummary(action,before,after);
  const value=(key:string,data:unknown,label:string)=>Array.isArray(data)&&data.length>3?<details className="history-value"><summary>{label}: {data.length} записей</summary><p>{show(key,data)}</p></details>:<span aria-label={label}>{label}: {show(key,data)}</span>;
  return <div className="history-changes">{changed.map(key=><div key={key}><strong>{names[key]}</strong>{value(key,before[key],'Было')}{value(key,after[key],'Стало')}</div>)}{successors&&<div><strong>Заменена работами</strong><span>{successors}</span></div>}{summary&&<p className="muted">{summary}</p>}<details><summary>Все данные версии</summary><pre>{JSON.stringify({было:before,стало:after},null,2)}</pre></details></div>;

}

function Export({ revision, revisions, readiness, rows, selectedIds, close, jobs }: {
  revision: Revision; revisions: Revision[]; readiness?: Schema["Readiness"];
  rows: Work[]; selectedIds: string[]; close: () => void; jobs: Schema['JobOut'][];
}) {
  const [draft, setDraft] = useState(true), [before, setBefore] = useState("");
  const [scope, setScope] = useState(emptyScope);
  const [documentId, setDocumentId] = useState("");
  const delivery = useDocumentDelivery(revision.id);
  const {building, discipline, category, group, description} = scope;
  const [useSelection, setUseSelection] = useState(selectedIds.length > 0);
  const exportState = {...scope, useSelection, selectedIds, document_id: documentId};
  const [format, setFormat] = useState<ExportFormat>("package"), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [ready, setReady] = useState<{key: string; url: string; filename: string} | null>(null);
  const [preparation,setPreparation]=useState<{id:string;key:string}|null>(null);
  const requestKey = JSON.stringify([revision.id, format, draft, before, useSelection,
    useSelection ? selectedIds : [documentId, building, discipline, category, group, description]]);
  const readyUrl = ready?.key === requestKey ? ready.url : null;
  const acceptedDelivery = jobs.map(targetedDelivery).find(Boolean);
  const history = useQuery({queryKey: ["exports", revision.id], queryFn: () => result(client.GET("/api/v1/revisions/{revision_id}/exports", {params:{path:{revision_id:revision.id}}}))});
  const pendingExports=jobs.filter(j=>j.kind==='export'&&['queued','running'].includes(j.status));
  const completedExports=jobs.filter(j=>j.kind==='export'&&j.status==='done').map(j=>j.id).join(',');
  useEffect(()=>{void history.refetch();},[completedExports]);
  const preparing=preparation!==null;
  useEffect(()=>{
    if(!preparation)return;
    const task=jobs.find(j=>j.id===preparation.id);
    if(!task)return;
    if(task.status==='done'){
      const url=task.details.download_url,filename=task.details.filename;
      if(typeof url==='string'&&url.startsWith('/api/v1/exports/')&&typeof filename==='string')setReady({key:preparation.key,url,filename});
      else setError('Комплект подготовлен. Проверьте сохранённые версии ниже.');
      setPreparation(null);void history.refetch();
    }else if(['failed','cancelled'].includes(task.status)){
      setError(task.message);setPreparation(null);void history.refetch();
    }
  },[jobs,preparation]);
  const protocol=useQuery({queryKey:['protocol',revision.id],queryFn:()=>result(client.GET('/api/v1/revisions/{revision_id}/protocol',{params:{path:{revision_id:revision.id}}}))});
  const included = includedWorks(rows, exportState, delivery.data);
  const {pending, unknown} = exportCounts(included);
  const hasDocumentReport = !useSelection && !!delivery.data?.documents.length &&
    (!!documentId || !Object.values(scope).some(Boolean));
  const includedIds=new Set(included.map(w=>w.id));
  const remarks=protocol.data?.filter(issue=>(!issue.work_id||includedIds.has(issue.work_id))).length;
  const selectionLabel=exportSelectionLabel(included);
  const options=(key:ScopeKey)=>scopeOptions(rows,scope,key).map(v=><option key={v}>{v}</option>);
  const download = async () => {
    setBusy(true); setError(""); setReady(null);
    try {
      const task = await call<Schema['JobOut']>(`/api/v1/revisions/${revision.id}/export-jobs`, {
        method: "POST",
        body: exportBody(exportState,{format,draft,before}),
      });
      setPreparation({id:task.id,key:requestKey});
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  return <Modal title="Выгрузить результат" close={close}>
    <p>{revision.name}</p>
    {acceptedDelivery && <section className="notice" aria-label="Принятый комплект проекта">
      <div><strong>Комплект проекта подготовлен для профессиональной проверки</strong>
        <p>Все исходные PDF проекта: Word, Excel, PDF, оба аудита и журнал исправлений. Нерешённых вопросов: {acceptedDelivery.questions}.</p>
        <a className="primary button-link" href={acceptedDelivery.href} download>Скачать готовый комплект (ZIP)<Download size={16}/></a>
        {acceptedDelivery.supplements && <p>Дополнение с предварительными расчётами и связью всех работ с исходниками: {acceptedDelivery.supplements.map(file=><a key={file.href} className="quiet button-link" href={file.href} download>{file.label}</a>)}</p>}
      </div>
    </section>}
    {acceptedDelivery && <p className="muted">Кнопка выше скачивает все объекты проекта. Ниже — создание отдельной выгрузки выбранной части «{revision.name}».</p>}
    {selectedIds.length > 0 && <label className="check-line"><input type="checkbox" checked={useSelection} onChange={e => setUseSelection(e.target.checked)} />Выбранные работы: {selectedIds.length}</label>}
    {useSelection&&selectionLabel&&<p className="muted">{selectionLabel}</p>}
    {!useSelection && <div className="export-scope">
      {!!delivery.data?.documents.length && <Field label="Исходный PDF"><select value={documentId} onChange={e => {setDocumentId(e.target.value); setScope(emptyScope);}}><DocumentOptions plan={delivery.data}/></select></Field>}
      <Field label="Здание / часть"><select value={building} onChange={e => setScope({...scope,building:e.target.value,description:""})}><option value="">Все здания</option>{options("building")}</select></Field>
      <Field label="Раздел"><select value={discipline} onChange={e => setScope({...emptyScope,building,discipline:e.target.value})}><option value="">Все разделы выбранной части</option>{options("discipline")}</select></Field>
      <Field label="Вид работ / подраздел"><select value={category} onChange={e => setScope({...emptyScope,building,discipline,category:e.target.value})}><option value="">Все виды работ</option>{options("category")}</select></Field>
      {category && <Field label="Тип / марка"><select value={group} onChange={e => setScope({...scope,group:e.target.value,description:""})}><option value="">Все типы / марки</option>{options("group")}</select></Field>}
      <Field label="Работа (наименование)"><select value={description} onChange={e => setScope({...scope,description:e.target.value})}><option value="">Все работы охвата</option>{options("description")}</select></Field>
    </div>}
    {!useSelection && documentId && <p className="notice">{documentNote(delivery.data, documentId)}</p>}
    {delivery.error && <p role="alert" className="error">Не удалось загрузить состав документов. {String(delivery.error)}</p>}
    <p className="export-summary"><strong>Работ в выбранной части: {included.length}</strong> · не подтверждено {pending} · объём неизвестен {unknown}{remarks!==undefined&&` · записей протокола ${remarks}`}</p>
    <Field label="Версия выгрузки"><select value={draft ? "draft" : "final"} onChange={e => setDraft(e.target.value === "draft")}><option value="draft">Промежуточная</option><option value="final">Итоговая</option></select></Field>
    <Field label="Что скачать"><select value={format} onChange={e => setFormat(e.target.value as ExportFormat)}>
      <option value="package">Выбранная часть — полный комплект (ZIP)</option>
      <option value="xlsx">ВОР с приложениями — Excel</option><option value="docx">ВОР с приложениями — Word</option>
      <option value="kac">Материалы и запрос КАЦ — Excel</option><option value="protocol">Только протокол расхождений — Excel</option><option value="information">Только информационная справка — Word</option>
      <option value="unknowns">Неизвестные и неподтверждённые данные — Excel с фильтром</option>
      <option value="grand">ВОР для ГРАНД-Сметы — Excel</option>
    </select></Field>
    <p className="muted">Неподтверждённые работы остаются в ведомости. Расхождения и недостающие данные включаются в протокол и информационную справку.</p>
    {['package','xlsx','docx','grand'].includes(format)&&<p className="muted">{delivery.data?.documents.length ? 'Для каждого исходного PDF — отдельная ВОР. Несколько сооружений внутри PDF остаются в одной ведомости. Файлы нескольких документов скачиваются одним ZIP; справочные и необработанные PDF содержат сведения о состоянии. Неоднозначные работы включаются один раз в отдельную ведомость.' : 'Для каждого раздела проекта — отдельная ВОР. Файлы нескольких разделов скачиваются одним ZIP.'}</p>}
    {format==='package'&&<p className="muted">В комплекте: ВОР, доступные сметные расчёты, протокол, справка, список неизвестных данных, запрос КАЦ и манифест с контрольными суммами. {packageIncludesSvor(exportState)?'СВОР разделов сметы входят последним выполненным раундом; разделы без раунда названы в справке.':'СВОР разделов сметы входят только в комплект по всему проекту.'}</p>}
    {['package','xlsx','docx'].includes(format)&&<p className="muted">«До изменений» — {before?'объём выбранной редакции проекта (сравнение редакций, не исходный документ)':baseText(revisions,revision.id)}. «С учётом изменений» — текущий объём.</p>}
    <details><summary>Сравнение редакций и полнота обработки</summary>
      <Field label="Сравнить с редакцией"><select value={before} onChange={e => setBefore(e.target.value)}><option value="">Исходный документ — без сравнения редакций</option>{revisions.filter(r => r.id !== revision.id && r.confirmed).map(r => <option value={r.id} key={r.id}>{r.name}</option>)}</select></Field>
      {readiness?.issues.length ? <ul>{readiness.issues.map((issue,i) => <li key={i}>{issue}</li>)}</ul> : <p>Ограничений обработки не зарегистрировано.</p>}
    </details>
    {format === "grand" && <p className="muted">Импорт нужно проверить в версии ГРАНД-Сметы Романа. Для обновления сохраняйте имя файла.</p>}
    {error && <p role="alert" className="error">{error}</p>}
    {pendingExports.map(task=><div key={task.id} className="notice" role="status"><div><strong>{task.message}</strong><p>Можно закрыть это окно. Готовый файл останется в истории выгрузок.</p><progress max={100} value={task.progress}/></div><button disabled={task.cancel_requested} onClick={()=>void result(client.POST('/api/v1/jobs/{job_id}/cancel',{params:{path:{job_id:task.id}}})).catch(e=>setError(String(e)))}>Отменить подготовку</button></div>)}
    {readyUrl && <p role="status">Файл готов и сохранён. Скачайте его по кнопке ниже.</p>}
    <footer><button onClick={close}>Закрыть</button>{readyUrl && !busy ? <a className="primary button-link" href={readyUrl} download={ready?.filename}>Скачать готовый файл<Download size={16}/></a> : <button className="primary" onClick={() => void download()} disabled={busy || !!preparing || delivery.isPending || !!delivery.error || (!included.length && !hasDocumentReport)}>{busy||preparing ? "Готовим комплект…" : "Подготовить файл"}<Download size={16}/></button>}</footer>
    {!!history.data?.length && <details className="export-history"><summary>Ранее выгруженные версии · {history.data.length}</summary>{history.data.map(item => <p key={item.id}><a href={`/api/v1/exports/${item.id}/download`} download={item.filename}>{new Date(item.created_at).toLocaleString("ru-RU")} · {item.draft ? "Промежуточная" : "Итоговая"} · {formatNames[item.format]||item.format} · {String(item.counts.works)} работ</a><small>{scopeText(item.scope)} · SHA-256 {item.sha256.slice(0,12)}</small></p>)}</details>}
  </Modal>;
}

export default function WorkbenchApp() {
  return <QueryClientProvider client={cache}><App /></QueryClientProvider>;
}

/** What the analysis checked in a category; its remarks about its own tools stay folded. */
function CoverageNotes({ notes }: { notes?: string[] }) {
  const { estimator, technical } = splitNotes(notes);
  return <>
    {estimator.map((note, i) => <p key={i}>{note}</p>)}
    {technical.length > 0 && <details className="analysis-technical"><summary>Технический журнал анализа · {technical.length}</summary>
      {technical.map((note, i) => <p className="muted" key={i}>{note}</p>)}</details>}
  </>;
}
